import { isoDateTime, z } from "../zod.js";
import { SERVER_TEXT_LOCALES, localeKeys, type ServerTextLocale } from "../locales.js";
import { Uuid } from "../identity/admin-schemas.js";

// Module 07 — Quality: the contract DTOs for slice 07-A, THE SPINE (spec 07
// §2.1–§2.6 + the §5 control-point, failure-cause, vertical-pack-seed,
// inspection and ship-readiness rows), slice 07-B-1, QUARANTINE HOLDS (§2.7 +
// the §5 holds list / manual-place / detail rows), and slice 07-B-2, THE
// DISPOSITION SURFACE (the §3.5 hold-disposition and inspection-resolve
// rows). The guided cut (§2.8/§2.9) and certificates (§2.10–§2.12) are slices
// 07-C and 07-E. Their DTOs are ABSENT, not stubbed — §0.2's own slice
// statement, and the statement slices 04-A and 05-A both wrote.
//
// CONVENTIONS, carried from inventory/waste/production:
//
//  * EXACTNESS (D-041). Every quantity, measurement, length and percentage on
//    this wire is a DECIMAL STRING mirroring its column's numeric(p,s) — the
//    exact text Postgres returns, never a JSON number. A measurement is graded
//    against min/max and a factory's money follows from the verdict; a float in
//    the request is precisely what the exact-arithmetic doctrine keeps out of
//    that decision. Three scales appear here and they are NOT interchangeable:
//    measurements are numeric(18,6) (§2.3/§2.6), cable lengths numeric(12,3)
//    metres (§2 conventions), inspected quantities numeric(14,3).
//  * Money is INTEGER MINOR UNITS + `currency char(3)`, and the ONE money
//    field on this wire is §2.7's `value_at_risk_minor` (QualityHold). Its
//    visibility was ADJUDICATED 2026-08-17 (D-126, owner — closing D-123
//    ruling 10, seam C10): the originating module governs its own number, so
//    spec 07 §7's set rules here — group_admin / facility_admin / finance /
//    qc / planner — role-keyed in the serializer, no field-grant code, and
//    module 05's shipped `WASTE_COST_ROLES` stays exactly as certified.
//    Redaction is ABSENCE — no key at all — following modules 04 and 05 and
//    never module 06's `null` + `redacted: true`; see the pair itself for the
//    currency divergence D-126 also wrote down.
//  * Lists answer `{items: [...]}`, with `next_cursor` on the unbounded ones —
//    never a bare array (the module-01 defect `catalogue-list-envelopes
//    .int.test.ts` was built for). The two CATALOG lists (control points,
//    failure causes) are deliberately unpaginated: they are bounded definition
//    sets the floor PWA must hold WHOLE to render an inspection offline, and a
//    paged catalog means a device that has "some of the checks".
//  * `{en, ru, zh, fr}` locale bundles with `en` required where the text IS the
//    row's identity (a failure cause's name), and all-optional where the text
//    is an addendum (a control point's operator instructions).

// --- shared scalars --------------------------------------------------------

/** A control-point code — stable across versions, e.g. `CP-CONT-FINAL` (§2.1).
 * Wider than the platform's UPPER_SNAKE catalog codes by exactly the dash,
 * which is §2.1's own spelling. */
export const QualityControlPointCode = z
  .string()
  .regex(/^[A-Z][A-Z0-9_-]{1,47}$/)
  .openapi({ example: "CP-CONT-FINAL", description: "Stable control-point code" });

/** A failure-cause code (§2.4) — the module-05 `WasteCode` grammar, restated
 * rather than imported so a change to another module's catalog contract cannot
 * silently widen what this one accepts. */
export const QualityFailureCauseCode = z
  .string()
  .regex(/^[A-Z][A-Z0-9_]{1,47}$/)
  .openapi({ example: "CONTINUITY_FAULT", description: "Stable UPPER_SNAKE cause code" });

/** A `numeric(18,6)` measurement as exact decimal text (D-041). SIGNED: a
 * measured deviation, an offset or a temperature reading can be negative, and a
 * bound that could not express one would silently exclude those check items. */
export const QualityMeasurement = z
  .string()
  .regex(/^-?\d{1,12}(\.\d{1,6})?$/)
  .openapi({ example: "2.050000", description: "Exact decimal, ≤ 6 dp (numeric(18,6))" });

/** A `numeric(12,3)` length in metres as exact decimal text (§2 conventions). */
export const QualityLengthM = z
  .string()
  .regex(/^\d{1,9}(\.\d{1,3})?$/)
  .openapi({ example: "49.500", description: "Exact decimal metres, ≤ 3 dp (numeric(12,3))" });

/** A strictly positive `numeric(12,3)` length — a fault position is measured
 * FROM the A-end, so zero is not a position on the cable. */
export const QualityPositiveLengthM = z
  .string()
  .regex(/^(?=.*[1-9])\d{1,9}(\.\d{1,3})?$/)
  .openapi({ example: "50.000", description: "Exact decimal metres > 0, ≤ 3 dp" });

/**
 * A `numeric(18,6)` length in metres as exact decimal text — MODULE 06's OWN
 * SCALE, and the scale every guided-cut length rides at (D-156, 2026-08-21).
 *
 * §9.6 makes module 06's MEASURED length the authority for all cut maths, and
 * module 06 stores it `numeric(18,6)`. At `QualityLengthM`'s three decimals a
 * drum measured 1543.500250 m cannot round-trip: the rounded pieces either land
 * inside module 06's `cut_tolerance_pct` — real metres silently written off as
 * a tolerance loss — or trip its `409 CUT_EXCEEDS_REMAINING`. Both failures are
 * invisible until a customer weighs the drum.
 *
 * `fault_position_m` and `trim_allowance_m` deliberately stay `QualityLengthM`
 * (3 dp): they are an operator's metre-mark reading and a control-point
 * setting, at the same scale `qc_inspection_results.fault_position_m` already
 * stores, so segment A (`fault − trim`) is 3 dp and segment B carries the 6-dp
 * remainder — and the tiling stays exact at BOTH scales.
 */
export const QualityMeasuredLengthM = z
  .string()
  .regex(/^\d{1,12}(\.\d{1,6})?$/)
  .openapi({ example: "49.500000", description: "Exact decimal metres, ≤ 6 dp (numeric(18,6))" });

/** A strictly positive `numeric(18,6)` length (D-156). A source cable and a cut
 * segment both have length; `from_m` may be 0 and takes the unsigned form above. */
export const QualityPositiveMeasuredLengthM = z
  .string()
  .regex(/^(?=.*[1-9])\d{1,12}(\.\d{1,6})?$/)
  .openapi({ example: "49.500000", description: "Exact decimal metres > 0, ≤ 6 dp" });

/** A `numeric(14,3)` inspected quantity as exact decimal text (§2.5). */
export const QualityQty = z
  .string()
  .regex(/^(?=.*[1-9])\d{1,11}(\.\d{1,3})?$/)
  .openapi({ example: "100.000", description: "Exact decimal quantity > 0, ≤ 3 dp" });

/** A `numeric(5,2)` sampling percentage 0 < pct ≤ 100 (§2.1). */
export const QualitySamplePct = z
  .string()
  .regex(/^(?=.*[1-9])\d{1,3}(\.\d{1,2})?$/)
  .refine((v) => Number(v) <= 100, { message: "must be ≤ 100" })
  .openapi({ example: "10.00", description: "Exact decimal percentage 0 < p ≤ 100, ≤ 2 dp" });

/**
 * A localized display name where the text IS the row's identity (§2.4
 * `name_i18n`). `en` is REQUIRED and every other locale optional, matching the
 * DB CHECK — and demanding all four would produce FAKE TRANSLATIONS, because an
 * operator adding a cause at 21:00 types the English string into every box to
 * get past the form and the product can never again tell a translation from a
 * placeholder. Missing locales fall back to `en` at render, visibly marked.
 */
export const QualityLocaleName = z
  .object({
    ...localeKeys(z.string().min(1).max(200).optional()),
    en: z.string().min(1).max(200),
  })
  .openapi("QualityLocaleName");

/** Operator instructions per locale (§2.1 `instructions_i18n`) — every locale
 * optional, including `en`: a control point need not carry instructions at all,
 * and `{}` is its honest empty value. */
export const QualityLocaleText = z
  .object(localeKeys(z.string().max(4000).optional()))
  .openapi("QualityLocaleText");

/**
 * The NAME-bundle overlay a create/version request may carry beside `name`
 * (0042, #128): every locale optional — `en` rides `name`, so requiring it
 * here would make clients state one string twice and give the two a way to
 * disagree — but bounded at the NAME length, not the instructions length.
 * Without this schema the request half was 20× wider than the response half
 * ({@link QualityLocaleName}'s 200), so a client could store a 4 000-char
 * Russian title the response schema then emits against its own published
 * bound, and the floor queue card would be handed a title the English path
 * structurally cannot produce.
 *
 * No `minLength`, deliberately: the editor sends `""` for a cleared locale box
 * and the server compacts it to absent (`compactLocale`) — a 400 on an empty
 * string would turn "clear this translation" into an error.
 */
export const QualityLocaleNameOverlay = z
  .object(localeKeys(z.string().max(200).optional()))
  .openapi("QualityLocaleNameOverlay");

// --- closed vocabularies (spec 07 §2) --------------------------------------

export const QualityStage = z.enum(["receiving", "in_process", "final"]);

/** Where the definition came from (§2.1). `vertical_pack` rows are written by
 * the pack seed alone — the API always creates `custom`. */
export const QualityControlPointSource = z.enum(["vertical_pack", "custom"]);

/** `automatic` points create inspections from the §3.2 triggers; `manual` ones
 * appear only through "New inspection" (§2.1). */
export const QualityTriggerMode = z.enum(["automatic", "manual"]);

/** §2.1 / §3.3. `aql` is ISO 2859-1 single sampling, normal inspection (D-025 —
 * an owner scope upgrade that put AQL in v1; D-123 ruling 11(c) deleted §11's
 * contradicting sentence). The PLAN LOOKUP and the sampler are slice 07-D's;
 * the vocabulary ships with the column that stores it. */
export const QualitySampleMode = z.enum([
  "every_unit",
  "fixed_count",
  "percentage",
  "first_n",
  "aql",
]);

/** ISO 2859-1 general inspection level (§2.1, D-025). */
export const QualityAqlInspectionLevel = z.enum(["I", "II", "III"]);

/** Acceptable quality limit (§2.1, D-025) — a DECIMAL STRING from a closed set,
 * never a number: `1.0` and `1` are the same float and different AQL plans. */
export const QualityAqlValue = z.enum(["0.65", "1.0", "1.5", "2.5", "4.0", "6.5"]);

export const QualityCheckType = z.enum(["pass_fail", "measurement", "photo", "checklist"]);

export const QualityFailureCauseCategory = z.enum([
  "material",
  "machine",
  "method",
  "operator",
  "environment",
  "unknown",
]);

/** §3.4's state machine: `open → in_progress → passed | failed | cancelled`. */
export const QualityInspectionStatus = z.enum([
  "open",
  "in_progress",
  "passed",
  "failed",
  "cancelled",
]);

/** Post-failure resolution, tracked separately from `status` (§3.4). */
export const QualityResolutionStatus = z.enum(["none", "pending", "resolved"]);

/** Which §3.5 workflow resolved a failure. Published whole because the ENUM is
 * the wire contract: 07-B/07-C produce `rework`/`scrap`/`hold_disposition`/
 * `accept_deviation`/`guided_cut`, and a client that already knows the value
 * can render the resolution the day those slices start writing it. */
export const QualityResolutionKind = z.enum([
  "rework",
  "scrap",
  "hold_disposition",
  "guided_cut",
  "accept_deviation",
]);

/** How the inspection came to exist (§2.5). The two retest origins are excluded
 * from FPY first-time counts (§2.12) — which is why the database makes
 * `origin IN ('cut_retest','rework_retest')` and a non-null
 * `retest_of_inspection_id` the same statement. */
export const QualityInspectionOrigin = z.enum(["trigger", "manual", "cut_retest", "rework_retest"]);

/** A recorded verdict on one check item (§2.6). `na` is how an OPTIONAL item is
 * skipped; a required item can never be `na`. */
export const QualityResultValue = z.enum(["pass", "fail", "na"]);

/** The installable vertical packs (`packages/verticals` `PACK_KEYS`), restated
 * here rather than imported because `@assessly/contracts` deliberately depends
 * on nothing but zod. Spec 07 §5's `pack='cable'` names no shipped pack — the
 * keys are `generic` and `cable-wire`, and `cable-wire` is the one whose
 * `qc_checklists` and `failure_causes` collections §3.1 seeds from. */
export const QualityVerticalPackKey = z.enum(["generic", "cable-wire"]);

const Timestamp = z.string().openapi({ format: "date-time" });
const Cursor = z.string().nullable();

// ===========================================================================
// §2.3 — check items
// ===========================================================================

export const QualityCheckItem = z
  .object({
    id: Uuid,
    seq: z.number().int(),
    /**
     * The item's name in the platform's four locales (§2.3; column added by
     * migration 0042, defect #128). THE FIELD A CLIENT RENDERS.
     *
     * The cable pack ships `{"en":"Diameter","ru":"Диаметр",…}` and until 0042
     * only the English survived the install, so an Almaty inspector typed a
     * measurement into a row labelled in a language they do not read. Same
     * bundle shape, same `en`-required rule and same client-side resolution as
     * §2.4's `name_i18n` — one path, not two.
     */
    name_i18n: QualityLocaleName,
    /** `name_i18n.en`, and since 0042 it is a GENERATED column so it cannot be
     * anything else. THE PRE-0042 FIELD, kept because removing a required
     * response property is a breaking change; new clients read `name_i18n`. */
    name: z.string(),
    check_type: QualityCheckType,
    /** Module 01 UoM code; present (and required) on measurement items. */
    uom: z.string().nullable(),
    min_value: QualityMeasurement.nullable(),
    target_value: QualityMeasurement.nullable(),
    max_value: QualityMeasurement.nullable(),
    /** `[{key, label:{en,ru,zh,fr}}]` for `check_type='checklist'`. */
    checklist_items_i18n: z.array(z.record(z.string(), z.unknown())).nullable(),
    photo_required: z.boolean(),
    min_photos: z.number().int(),
    is_required: z.boolean(),
    /** Measurement rows flagged true are printed on CoA documents (07-E). */
    appears_on_coa: z.boolean(),
  })
  .openapi("QualityCheckItem");

export const QualityCheckItemInput = z
  .object({
    seq: z.number().int().min(0).max(999),
    /** The item's ENGLISH name. Required, and it is always what `name_i18n.en`
     * becomes on the row — see `QualityControlPointCreateRequest.name_i18n` for
     * the whole of the two-field rule. */
    name: z.string().min(1).max(200),
    /** The item's OTHER locales, carried through an edit (0042, #128). Optional;
     * any `en` in it is overridden by `name` above. */
    name_i18n: QualityLocaleNameOverlay.optional(),
    check_type: QualityCheckType,
    uom: z.string().min(1).max(16).optional(),
    min_value: QualityMeasurement.optional(),
    target_value: QualityMeasurement.optional(),
    max_value: QualityMeasurement.optional(),
    checklist_items_i18n: z.array(z.record(z.string(), z.unknown())).min(1).optional(),
    photo_required: z.boolean().optional(),
    min_photos: z.number().int().min(1).max(20).optional(),
    is_required: z.boolean().optional(),
    appears_on_coa: z.boolean().optional(),
  })
  .superRefine((item, ctx) => {
    // §2.3: a measurement item needs a unit and at least one bound. Without a
    // bound there is nothing for §3.4's auto-grading to compute a verdict
    // against, and the operator is asked for a number the system cannot judge.
    if (item.check_type === "measurement") {
      if (item.uom === undefined) {
        ctx.addIssue({
          code: "custom",
          path: ["uom"],
          message: "measurement items require a uom (spec 07 §2.3)",
        });
      }
      if (item.min_value === undefined && item.max_value === undefined) {
        ctx.addIssue({
          code: "custom",
          path: ["min_value"],
          message: "measurement items require min_value and/or max_value (spec 07 §2.3)",
        });
      }
    }
    if (item.check_type === "checklist" && item.checklist_items_i18n === undefined) {
      ctx.addIssue({
        code: "custom",
        path: ["checklist_items_i18n"],
        message: "checklist items require at least one row (spec 07 §2.3)",
      });
    }
    if (
      item.min_value !== undefined &&
      item.max_value !== undefined &&
      Number(item.min_value) > Number(item.max_value)
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["max_value"],
        message: "min_value must be ≤ max_value (spec 07 §2.3)",
      });
    }
  })
  .openapi("QualityCheckItemInput");

// ===========================================================================
// §2.2 — bindings
// ===========================================================================

/**
 * What a control point applies to. RECEIVING binds material and/or supplier;
 * FINAL binds a product; IN-PROCESS POINTS ARE NOT BOUND HERE AT ALL — that
 * binding is module 01's `routing_steps.qc_control_point_id` (01 §2.11), set in
 * the Catalogue routing editor. The rule is enforced by a DB trigger as well as
 * the service (§2.2), because it reads the parent's stage.
 *
 * `supplier_id` names module 10, which does not exist: it is NULL on every P2
 * row and fills additively when 10 lands (§0.1).
 */
export const QualityControlPointBinding = z
  .object({
    id: Uuid,
    material_id: Uuid.nullable(),
    supplier_id: Uuid.nullable(),
    product_id: Uuid.nullable(),
  })
  .openapi("QualityControlPointBinding");

export const QualityControlPointBindingInput = z
  .object({
    material_id: Uuid.optional(),
    supplier_id: Uuid.optional(),
    product_id: Uuid.optional(),
  })
  .openapi("QualityControlPointBindingInput");

// ===========================================================================
// §2.1 — control points
// ===========================================================================

/** One versioned control-point definition, WITH its bindings and check items.
 * The list and the detail return the same shape: a control point without its
 * checks is not a definition of anything, and the collection is bounded (the
 * cable pack ships four) — §4.1's library table renders a bindings summary from
 * exactly this payload. */
export const QualityControlPoint = z
  .object({
    id: Uuid,
    /** NULL = applies at every facility (§2.1). */
    facility_id: Uuid.nullable(),
    code: QualityControlPointCode,
    version: z.number().int(),
    is_current: z.boolean(),
    /** The point's name in all four locales (§2.1; 0042, defect #128) — THE
     * FIELD A CLIENT RENDERS, and the one the floor queue reads so a Russian
     * inspector sees "Входной контроль меди" rather than "Copper receiving". */
    name_i18n: QualityLocaleName,
    /** `name_i18n.en` — the pre-0042 field, GENERATED on the row. */
    name: z.string(),
    stage: QualityStage,
    source: QualityControlPointSource,
    trigger_mode: QualityTriggerMode,
    sample_mode: QualitySampleMode,
    sample_size: z.number().int().nullable(),
    sample_pct: QualitySamplePct.nullable(),
    aql_inspection_level: QualityAqlInspectionLevel.nullable(),
    aql_value: QualityAqlValue.nullable(),
    /** What "blocking" fences differs by stage and is NOT a downstream-operation
     * guard (D-123 ruling 5): receiving holds the received stock, in-process
     * sets module 04's operation `qc_hold` (which fences REWORK on a re-opened
     * step and nothing else), final refuses shipment allocation in module 09. */
    is_blocking: z.boolean(),
    auto_hold_on_fail: z.boolean(),
    allow_guided_cut: z.boolean(),
    cut_trim_allowance_m: QualityLengthM,
    min_sellable_length_m: QualityLengthM.nullable(),
    instructions_i18n: QualityLocaleText,
    active: z.boolean(),
    superseded_by_id: Uuid.nullable(),
    bindings: z.array(QualityControlPointBinding),
    check_items: z.array(QualityCheckItem),
    created_at: Timestamp,
    updated_at: Timestamp,
  })
  .openapi("QualityControlPoint");

/**
 * Create version 1 (§5 row 2) — and, on `POST /:id/versions`, version N+1 with
 * the edits (§5 row 4 says in as many words that it takes "the same body as
 * create").
 *
 * `source`, `version`, `is_current` and `superseded_by_id` are absent BY
 * CONSTRUCTION: a pack-owned row is written by the seed alone, and the version
 * chain is the server's bookkeeping. Expressing that as "the field does not
 * exist" is a rule no client can get wrong, which is the shape module 05 gave
 * its non-editable `code`.
 */
export const QualityControlPointCreateRequest = z
  .object({
    facility_id: Uuid.optional(),
    code: QualityControlPointCode,
    /**
     * The point's ENGLISH name. Required — §4.2's editor has one name box, and
     * `{en: <that box>}` is the honest bundle for a point a tenant authored.
     */
    name: z.string().min(1).max(200),
    /**
     * The point's OTHER locales (0042, defect #128). Optional, and the rule is
     * one line: **`name` is the English, always; anything under `en` here is
     * overridden by it.**
     *
     * It exists for the case that would otherwise UNDO the fix. §5 row 4's
     * version route takes "the same body as create", so a qc lead correcting an
     * English typo on a PACK point would send `name` alone and the successor's
     * bundle would collapse to `{en: …}` — the Almaty floor losing the Russian
     * the pack shipped, through a screen nobody thought was about translations.
     * The editor sends back the bundle it loaded and they survive the edit.
     * Authoring the Russian ITSELF is a §4.2 change and is not this field.
     *
     * `QualityLocaleNameOverlay` (every locale optional, NAME-bounded) rather
     * than `QualityLocaleName`: `en` is carried by `name`, so requiring it here
     * would make clients state the same string twice and give them a way to
     * disagree — and rather than `QualityLocaleText`, whose 4 000-char
     * instructions bound would let a request store a title five times longer
     * than the response schema admits.
     */
    name_i18n: QualityLocaleNameOverlay.optional(),
    stage: QualityStage,
    trigger_mode: QualityTriggerMode.optional(),
    sample_mode: QualitySampleMode.optional(),
    sample_size: z.number().int().min(1).max(100000).optional(),
    sample_pct: QualitySamplePct.optional(),
    aql_inspection_level: QualityAqlInspectionLevel.optional(),
    aql_value: QualityAqlValue.optional(),
    is_blocking: z.boolean().optional(),
    auto_hold_on_fail: z.boolean().optional(),
    allow_guided_cut: z.boolean().optional(),
    cut_trim_allowance_m: QualityLengthM.optional(),
    min_sellable_length_m: QualityPositiveLengthM.optional(),
    instructions_i18n: QualityLocaleText.optional(),
    /** Empty for an in-process point — §2.2 forbids binding one here. */
    bindings: z.array(QualityControlPointBindingInput).max(200).optional(),
    check_items: z.array(QualityCheckItemInput).min(1).max(100),
  })
  .superRefine((body, ctx) => {
    // §2.1's "required when sample_mode = …" clauses, in BOTH directions —
    // the same grain the DB CHECK carries. A row that says `every_unit` while
    // holding a sample size would give §3.3 two answers to one question.
    const mode = body.sample_mode ?? "every_unit";
    const needsSize = mode === "fixed_count" || mode === "first_n";
    if (needsSize && body.sample_size === undefined) {
      ctx.addIssue({
        code: "custom",
        path: ["sample_size"],
        message: `sample_size is required when sample_mode='${mode}' (spec 07 §2.1)`,
      });
    }
    if (!needsSize && body.sample_size !== undefined) {
      ctx.addIssue({
        code: "custom",
        path: ["sample_size"],
        message: `sample_size is only accepted when sample_mode is 'fixed_count' or 'first_n' (spec 07 §2.1)`,
      });
    }
    if (mode === "percentage" && body.sample_pct === undefined) {
      ctx.addIssue({
        code: "custom",
        path: ["sample_pct"],
        message: "sample_pct is required when sample_mode='percentage' (spec 07 §2.1)",
      });
    }
    if (mode !== "percentage" && body.sample_pct !== undefined) {
      ctx.addIssue({
        code: "custom",
        path: ["sample_pct"],
        message: "sample_pct is only accepted when sample_mode='percentage' (spec 07 §2.1)",
      });
    }
    const aql = mode === "aql";
    if (aql && (body.aql_inspection_level === undefined || body.aql_value === undefined)) {
      ctx.addIssue({
        code: "custom",
        path: ["aql_value"],
        message:
          "aql_inspection_level and aql_value are required when sample_mode='aql' (spec 07 §2.1, D-025)",
      });
    }
    if (!aql && (body.aql_inspection_level !== undefined || body.aql_value !== undefined)) {
      ctx.addIssue({
        code: "custom",
        path: ["aql_value"],
        message: "the AQL plan is only accepted when sample_mode='aql' (spec 07 §2.1)",
      });
    }
    // §2.3: check-item order is the execution order, so it must be a real
    // order — two items at seq 3 render in whichever order the database felt
    // like, on a screen a worker follows top to bottom.
    const seqs = body.check_items.map((i) => i.seq);
    if (new Set(seqs).size !== seqs.length) {
      ctx.addIssue({
        code: "custom",
        path: ["check_items"],
        message: "check-item seq values must be unique within a control point (spec 07 §2.3)",
      });
    }
  })
  .openapi("QualityControlPointCreateRequest");

export const QualityControlPointListResponse = z
  .object({ items: z.array(QualityControlPoint) })
  .openapi("QualityControlPointListResponse");

// ===========================================================================
// §2.4 — failure causes
// ===========================================================================

/** A module-05 waste-cause code, as module 07 addresses it (D-132): the value of
 * `waste_cause_codes.code`, plain text, service-validated against module 05's
 * own catalog, never an FK. The grammar is module 05's `WasteCode`, RESTATED
 * rather than imported so a change to another module's catalog contract cannot
 * silently widen what this one accepts (the rule `QualityFailureCauseCode`
 * states above, applied to the same target catalog). */
export const QualityWasteCauseCode = z
  .string()
  .regex(/^[A-Z][A-Z0-9_]{1,47}$/)
  .openapi({ example: "MACHINE_DRIFT", description: "Module-05 waste cause code (D-132)" });

export const QualityFailureCause = z
  .object({
    id: Uuid,
    code: QualityFailureCauseCode,
    name_i18n: QualityLocaleName,
    category: QualityFailureCauseCategory,
    /**
     * The D-132 QC-cause → waste-cause mapping (exposed 2026-08-20, fix #117 —
     * the read half D-132 pt 6 reserved for "its own contracts change"; this is
     * that change, and it is FORCED rather than opportunistic: a screen that
     * cannot read the column cannot show a qc which of their causes will refuse
     * a scrap, and the mapping route below would be a control with no readout).
     *
     * `null` IS THE MEANING, not a redaction. Every other absent-vs-null
     * question in this module is about money and answers "absent" (§7's
     * redaction rule); this field is not role-keyed at all — the catalog read
     * carries no permission code — and `null` is a fact the glass must render:
     * this cause is UNMAPPED and a scrap resolution against it answers 422
     * `QUALITY_SCRAP_CAUSE_UNMAPPED` (D-132 pt 4). Absence would make "unmapped"
     * indistinguishable from "an older server that did not send the field".
     */
    waste_cause_code: QualityWasteCauseCode.nullable(),
    active: z.boolean(),
    created_at: Timestamp,
    updated_at: Timestamp,
  })
  .openapi("QualityFailureCause");

export const QualityFailureCauseCreateRequest = z
  .object({
    code: QualityFailureCauseCode,
    name_i18n: QualityLocaleName,
    category: QualityFailureCauseCategory,
    /** The D-132 QC-cause → waste-cause mapping (07-B-2 fix round, R6 —
     * additive): module 05's `waste_cause_codes.code`, service-validated
     * against that catalog at create time. Optional — an unmapped cause is
     * legal and refuses scrap resolution (422 QUALITY_SCRAP_CAUSE_UNMAPPED).
     * Since fix #117 this is no longer the ONLY remedy: an EXISTING cause is
     * mappable through `PUT /v1/quality/failure-causes/{id}/waste-cause`,
     * which is the door a pre-0037 pack row (D-132 pt 5) has to come through.
     * Same grammar as module 05's `WasteCode`, restated not imported. */
    waste_cause_code: QualityWasteCauseCode.optional(),
  })
  .openapi("QualityFailureCauseCreateRequest");

/**
 * The mapping write, and the whole of it (fix #117 — §5 row 8a).
 *
 * A failure cause is otherwise IMMUTABLE by design: `code` is the identity
 * every analytics grouping and every pack re-install addresses the row by,
 * `category` is the Pareto's axis, and `name_i18n` is printed on inspections
 * already closed. So this is not an update request with one field in it — it is
 * a request body for the SUB-RESOURCE `…/{id}/waste-cause`, and the URL is what
 * makes the immutability a property of the route rather than a whitelist inside
 * a handler somebody later widens.
 *
 * NO CLEAR, DELIBERATELY: the field is required and non-null. Un-mapping a
 * mapped cause would manufacture the exact dead end this route exists to close
 * (D-132 pt 4's refusal), and the deliberately-unmapped set — cable-wire's
 * PRINT_ILLEGIBLE and MOISTURE, generic's MACHINE_FAULT and STORAGE_CONDITION —
 * is authored in PACK DATA (doctrine 5) and at create time, not by blanking a
 * live row.
 */
export const QualityFailureCauseWasteCauseRequest = z
  .object({ waste_cause_code: QualityWasteCauseCode })
  .openapi("QualityFailureCauseWasteCauseRequest");

export const QualityFailureCauseListResponse = z
  .object({ items: z.array(QualityFailureCause) })
  .openapi("QualityFailureCauseListResponse");

// ===========================================================================
// §3.1 — vertical-pack seeding
// ===========================================================================

export const QualityVerticalPackSeedRequest = z
  .object({ pack: QualityVerticalPackKey })
  .openapi("QualityVerticalPackSeedRequest");

/**
 * §9.15: seeding is idempotent per `code` — which is why the response reports
 * the halves separately rather than one total that hides the difference.
 *
 * D-205 splits that report in two, because the rule is not "an existing row is
 * skipped" but "a row the tenant OWNS is skipped". A pack-owned point
 * (`source='vertical_pack'` — nobody has edited it) whose definition has drifted
 * from the pack CONVERGES onto the pack as a NEW VERSION, and that is counted
 * where an operator can see it: a convergence nobody can see is the silent
 * overwrite D-033 forbids, and a convergence nobody can perform is a corrected
 * pack that cannot reach an installed tenant (the four-month `CP-RECV-CU` AQL
 * defect, D-204). `control_points_custom_skipped` is the other half of the same
 * fact — the rows a tenant edited, which the pack no longer describes and never
 * touches.
 */
export const QualityVerticalPackSeedResponse = z
  .object({
    control_points_created: z.number().int(),
    /** D-205 — pack-owned codes whose definition differed from the pack and
     * were re-minted at `version + 1` ONTO it. Inspections pinned to the old
     * version keep executing the old definition (§3.1 step 5, D-164). */
    control_points_converged: z.number().int(),
    /** Left exactly as they were: already identical to the pack, tenant-owned,
     * or fenced by a refusal the seeder logs (a pack that moved a code to
     * another stage, or a concurrent version bump). */
    control_points_skipped: z.number().int(),
    /** The subset of `control_points_skipped` whose `source` is `custom` — the
     * D-033 half, broken out so it is visible rather than summed away. */
    control_points_custom_skipped: z.number().int(),
    check_items_created: z.number().int(),
    bindings_created: z.number().int(),
    failure_causes_created: z.number().int(),
    failure_causes_skipped: z.number().int(),
    /** Pack causes whose already-installed row has no `waste_cause_code`
     * although the pack states one (the D-132 pt 5 hole). §2.4 carries NO
     * ownership discriminator — no `source`, no version chain, no seed link —
     * so a cause row is reported rather than converged: overwriting it would
     * revert a mapping a manager set deliberately through
     * `PUT /v1/quality/failure-causes/{id}/waste-cause`. Non-zero means a §3.5b
     * scrap on those causes refuses until somebody maps them there. */
    failure_causes_unmapped: z.number().int(),
    /** Every convergence, PER CODE (D-205 ruling 6) — a total cannot answer
     * "which definition did the platform move under my tenant". `changed` names
     * the §2.1 columns that actually differed, with `check_items` / `bindings`
     * standing for their whole collection (§2.3 rows have no identity across a
     * version, so "the checklist changed" is the finest true statement). */
    converged_points: z.array(
      z.object({
        code: QualityControlPointCode,
        from_version: z.number().int(),
        to_version: z.number().int(),
        changed: z.array(z.string()),
      }),
    ),
    /** Every code left alone FOR A REASON, with the reason on the wire rather
     * than hidden in a total. A pack-owned point that already matched the pack
     * is NOT listed: that is the no-op case and the common one, and six codes on
     * every clean re-seed would bury the one line an operator must act on. */
    retained_points: z.array(
      z.object({
        code: QualityControlPointCode,
        /** `tenant_owned` — `source='custom'`, the D-033 half.
         *  `stage_locked` — the pack moved the code to another stage, which is
         *  not a version of anything (§4.2's locked stage selector).
         *  `versioned_concurrently` — somebody versioned it mid-seed; their
         *  write wins and the next re-seed converges onto what they left.
         *  `plan_unapplicable` — #127/D-210: an `aql` receiving plan whose
         *  bindings resolve only to bulk-grained materials on this tenant; the
         *  point is reported rather than written, and installs on the next
         *  seed after the plan or the materials' tracking changes. */
        reason: z.enum([
          "tenant_owned",
          "stage_locked",
          "versioned_concurrently",
          "plan_unapplicable",
        ]),
      }),
    ),
    /** The §5 row 8a work list behind `failure_causes_unmapped`: the cause codes
     * a manager has to map by hand, because §2.4 has no ownership fence and the
     * seeder will not guess. A count is not actionable; a list is. */
    unmapped_failure_causes: z.array(QualityFailureCauseCode),
  })
  .openapi("QualityVerticalPackSeedResponse");

// ===========================================================================
// §2.6 — results
// ===========================================================================

export const QualityInspectionResult = z
  .object({
    id: Uuid,
    check_item_id: Uuid,
    /** NULL = not yet recorded (§2.6's eager-creation contract, which is what
     * lets the floor sync a partial inspection). */
    result: QualityResultValue.nullable(),
    measured_value: QualityMeasurement.nullable(),
    checklist_results: z.record(z.string(), z.boolean()).nullable(),
    /** → `files(id)`, purpose `quality_evidence` — NEVER raw S3 keys (D-064).
     * Applier-validated against the registry: same tenant, right purpose,
     * `status='stored'`, which is stricter than the foreign key PostgreSQL
     * cannot put on array elements anyway (§2.6, D-123 ruling 11(b)). */
    photo_file_ids: z.array(Uuid),
    fault_position_m: QualityLengthM.nullable(),
    recorded_by: Uuid.nullable(),
    recorded_at: Timestamp.nullable(),
  })
  .openapi("QualityInspectionResult");

/**
 * Record ONE result (§5). Repeatable and idempotent: the write is an UPSERT on
 * `(inspection_id, check_item_id)` — §2.6's identity — so a floor device that
 * flushes the same result twice converges on one row instead of appending a
 * second. There is no `client_idempotency_key` field here because there is no
 * column for one: the platform's required `Idempotency-Key` header carries the
 * response replay (§9.1's "200, not 409") and the UPSERT carries the row.
 *
 * `result` is OPTIONAL on a measurement item: §3.4 computes the verdict from
 * `measured_value` against the item's min/max and echoes it back. Where the
 * operator does send one, a computed FAIL can never be overridden to pass
 * (M07-S09) — a computed pass may be manually failed with a note, which is
 * visual judgment and the only direction that adds information.
 */
export const QualityInspectionResultRequest = z
  .object({
    check_item_id: Uuid,
    result: QualityResultValue.optional(),
    measured_value: QualityMeasurement.optional(),
    checklist_results: z.record(z.string(), z.boolean()).optional(),
    photo_file_ids: z.array(Uuid).max(20).optional(),
    fault_position_m: QualityPositiveLengthM.optional(),
    note: z.string().max(2000).optional(),
  })
  .openapi("QualityInspectionResultRequest");

export const QualityInspectionResultResponse = z
  .object({
    /** The stored row, with the COMPUTED `result` echoed (§5). */
    result: QualityInspectionResult,
    /** `open → in_progress` on the first result recorded (§3.4). */
    inspection_status: QualityInspectionStatus,
  })
  .openapi("QualityInspectionResultResponse");

// ===========================================================================
// §3.5d — the guided-cut ELIGIBILITY BLOCK carried on the inspection read
// (slice 07-C, D-158). Declared before §2.5 because the inspection detail
// response embeds it.
// ===========================================================================

/**
 * WHY a guided cut is not available on this inspection (D-158's `reason`,
 * rendered through §4.4's four not-available states).
 *
 * `null` when `eligible` is true. The four RENDER states map onto these nine
 * facts, and the mapping is stated here because D-167 makes it a rule rather
 * than a style: two dead controls with two different next steps get two
 * different sentences.
 *
 *  * ABSENT — "not an action on this check": `not_offered_by_control_point`
 *    and `subject_not_length_measured`. There is no cut to offer here and
 *    never was, so a dead control would imply one exists.
 *  * DEAD, with the reason inside the slab: `depth_cap` (§9.4 — "this drum is
 *    5 cuts from the original; scrap, downgrade or rework"), `no_fault_position`
 *    (§3.5d's eligibility — record where the fault is first) and the two
 *    ARITHMETIC refusals added by D-181, `source_too_short_to_cut` and
 *    `no_sellable_segment`, whose continuation is the scrap door. Depth is NOT
 *    a permission fact, and an absence would read, to a qc lead who HOLDS
 *    `quality.cut.execute` and saw the button on the previous bobbin, as a
 *    refusal of their authority (D-167) — and a drum that is merely too short
 *    is the same shape of fact.
 *  * DEAD, with the reason AND the fix in place: `length_not_measured` (§9.6
 *    — the re-measure is offered ON the sheet, never as a link away, because
 *    D-118's fix-at-the-control half exists precisely because a link throws
 *    away the form the operator is standing in) and `fault_not_on_source`,
 *    which takes the same treatment for the same reason: a re-measure that
 *    shrank the drum under the recorded mark is fixed by re-measuring or by
 *    correcting the mark, both on this sheet.
 *  * `inspection_not_actionable` is the ordinary lifecycle answer: the sheet
 *    renders only on a failed, resolution-pending inspection, so a client
 *    normally never sees it — it is on the wire so an agent or a stale tab
 *    reading the detail gets the same fact the screen would.
 *
 * **THE LAST THREE ARE THE GEOMETRY, AND THEY EXIST BECAUSE THE BLOCK ONCE
 * DISAGREED WITH `propose` (added 2026-08-21, D-181).** `eligible` asked six
 * questions and not the arithmetic one, so it published `eligible: true` on
 * inspections `POST /inspections/{id}/cut-events` then refused **422
 * `QUALITY_CUT_NOT_ELIGIBLE`** — and §4.4's fifth button renders from this
 * field and nothing else, so the control it describes is LIVE and 422s on tap:
 * D-118's dead control. The shape is the one the cable pack produces:
 * `CP-CONT-FINAL` ships
 * a 0.5 m trim and a 10 m minimum, so a 15 m off-cut with a fault at 5.0 m
 * splits into 4.5 m and 9.5 m, both demoted to waste, nothing sellable. The
 * server now runs the propose's OWN planner (`cut-plan.ts`) when a fault is
 * recorded, and its fault-independent length test (`length > trim + min`) when
 * one is not.
 *
 * ABSENCE FOR "NO GRANT" IS NOT HERE, deliberately: a principal without
 * `quality.cut.execute` sees no button at all (absence stays the permission
 * answer, D-118), and that is a fact the client's own permission mirror knows
 * without asking the server for a reason string.
 */
export const QualityGuidedCutIneligibleReason = z.enum([
  "not_offered_by_control_point",
  "subject_not_length_measured",
  "inspection_not_actionable",
  "no_fault_position",
  "length_not_measured",
  "depth_cap",
  "source_too_short_to_cut",
  "fault_not_on_source",
  "no_sellable_segment",
]);

/**
 * §3.5d's eligibility, computed SERVER-SIDE and carried on
 * `GET /v1/quality/inspections/{id}` (D-158 — the one field that unblocks
 * three otherwise-undrivable facts).
 *
 * Nothing on any endpoint carried genealogy depth or cut eligibility before
 * this: the only reachable fact was `parent_stock_unit_id` on module 06's unit
 * read, which made §9.4's cap a five-deep client-side walk that CANNOT RUN IN
 * AIRPLANE MODE AT ALL. The depth here is a BOUNDED (≤6) ancestor walk through
 * `StockUnitsService.findByIdInTxn` — module 06's own service, never its
 * tables, so §6's ownership claim stays statically checkable — and this read is
 * the floor's per-inspection cached read, i.e. the one read a phone is
 * guaranteed to hold offline.
 *
 * `captures_fault_position` IS DELIBERATELY NOT GATED ON `eligible`. The
 * operator types the fault position while the inspection is still
 * `in_progress` — before there is a failure to be eligible for — so this flag
 * is true on an OPEN inspection over a length-measured unit whose pinned point
 * allows a cut, and `eligible` only becomes true after a failed completion.
 * Two flags because they are true at two different moments. It is also the
 * field that finally gives `fault_position_m` a door: the execution screen's
 * old predicate was `isMeasurement && item.uom === "m"`, and `CP-CONT-FINAL` —
 * the ONLY pack point permitting a cut — has four items of kinds `pass_fail`,
 * `pass_fail`, a milliamp `measure` and a `photo`, not one of them denominated
 * in metres. Within such an inspection the field renders on `pass_fail` and
 * `measure` items; `photo` and `checklist` never (one records evidence, the
 * other a list of conditions, and neither is a metre mark).
 */
export const QualityInspectionGuidedCut = z
  .object({
    /**
     * Every §3.5d condition met AND the inspection is `failed` with the
     * resolution still pending **AND the cut this inspection would launch
     * actually keeps a sellable piece** (D-181 — the last clause was missing
     * and made this field's `true` a promise the propose route broke).
     *
     * The guarantee, stated as the invariant a test can hold: **`eligible:
     * true` means `POST /v1/quality/inspections/{id}/cut-events` with an empty
     * body succeeds.** The empty body is not a caveat — it is the request the
     * floor sends; a caller who overrides `fault_position_m` or raises
     * `trim_allowance_m` is asking about their own numbers, and neither
     * override can turn a `false` here into a success (a raised trim only
     * shrinks what is kept, and a fault higher up the cable grows a segment
     * that would then span the lower fault).
     */
    eligible: z.boolean(),
    /** `allow_guided_cut` on the PINNED point ∧ the subject is a
     * length-measured stock unit. True while the inspection is still open. */
    captures_fault_position: z.boolean(),
    /**
     * The SOURCE unit's genealogy depth. **The original unit is 0**, its
     * children 1, and a propose is refused `422 QUALITY_CUT_DEPTH_CAP` at
     * `>= 5` (§9.4, defined once in D-158 so nobody re-derives it).
     *
     * NULL when the inspection's subject is not a stock unit — a lot or a
     * work-order operation has no genealogy, and a `0` there would assert it
     * is an original drum. A wrong number looks like an answer and a null does
     * not (the D-088 ruling-2 rule this platform applies to
     * `produced_shift_instance_id` for the same reason). D-158's block writes
     * the field `int`; that is its shorthand for the case it is about — a unit
     * subject — and the nullable spelling is the honest form of the same fact,
     * recorded here rather than resolved by inventing a depth.
     */
    genealogy_depth: z.number().int().nullable(),
    /** Why not, when `eligible` is false; null when it is true. */
    reason: QualityGuidedCutIneligibleReason.nullable(),
    /** Module 06's MEASURED length of the subject unit (§9.6 — never the
     * machine-claimed one). NULL for a non-unit subject, and NULL for a unit
     * carrying only a claimed length, which is what `length_not_measured`
     * says. */
    source_length_m: QualityMeasuredLengthM.nullable(),
    /** The pinned point's `cut_trim_allowance_m` — the propose default, which
     * the operator may override UPWARD only. NULL when the point offers no
     * cut. */
    trim_allowance_m: QualityLengthM.nullable(),
    /** The pinned point's `min_sellable_length_m`. **NULL means NO MINIMUM**
     * (D-172): a control point that names no minimum imposes none, and every
     * stock segment stays stock however short. */
    min_sellable_length_m: QualityLengthM.nullable(),
    /** The LIVE (`proposed` or `confirmed`) cut on this inspection, or null.
     * THIS IS THE DISCOVERY MECHANISM (D-162): §5 has no list route for cut
     * events on purpose — the inspection is the thing the operator has in
     * their hand. */
    cut_event_id: Uuid.nullable(),
  })
  .openapi("QualityInspectionGuidedCut");

// ===========================================================================
// §2.5 — inspections
// ===========================================================================

/**
 * The sampling plan the sampler ACTUALLY RAN (§3.3, D-192) — which is not
 * always the plan the control point names.
 *
 * `lot_whole` is the one value with no counterpart in §2.1's `sample_mode`:
 * D-194 gives a BULK receipt (no `stock_units` at all — 3,000 kg of PVC
 * pellets, one lot, nothing to sample) exactly one lot-subject inspection
 * whatever the point's plan says, and records that fact here rather than
 * silently reporting the configured mode. A bulk receipt meeting an `aql`
 * point is REFUSED `422 QUALITY_SAMPLING_PLAN_INVALID` instead, because ISO
 * 2859-1 has no reading for a lot size in kilograms and a fabricated accept
 * number is a thing a factory then acts on.
 */
export const QualityAppliedSampleMode = z.enum([
  "every_unit",
  "fixed_count",
  "percentage",
  "first_n",
  "aql",
  "lot_whole",
]);

/**
 * THE RESOLVED PLAN, ECHOED (D-192, D-195). The operator reads *"sample 5 of
 * 50, accept 0"* instead of meeting an unexplained queue of five inspections.
 *
 * **The echo is not a convenience — it is the ONLY version that can be
 * trusted.** D-195 puts the AQL tables and the resolver in
 * `packages/domain-kernel`, which `apps/web` does not depend on, so a client
 * cannot recompute this; and even if it could, a recomputation answers a
 * DIFFERENT question than the one the sampler asked — `sample_lot_size` is not
 * recoverable after the fact (units get cut, scrapped, transferred and
 * consumed), and the accept number stops being derivable the moment the
 * control point is versioned. The server decided these numbers once; this is
 * that decision, read back.
 *
 * **THE FOUR NUMBERS ARE AQL-ONLY, and their absence under any other mode is
 * information rather than a gap.** `fixed_count`, `percentage` and `first_n`
 * take their plan from the PINNED control-point version (`sample_size` /
 * `sample_pct` on an immutable row reachable through `control_point_id`), so
 * persisting it here would be a second copy of a fact that already has an
 * owner and a second place for it to be wrong. Only AQL's numbers are a
 * function of a lot size nothing else preserves. The DB says the same thing in
 * the same words (`qc_inspections_sampling_grain`, migration 0040).
 *
 * **`sample_seed` IS NOT ON THE WIRE.** D-191 persists it so the answer to
 * *"why was bag 7 not inspected"* is a replay rather than a shrug — but the
 * replay is the SERVER's, run against the kernel that made the original pick.
 * A client holding the seed could not reproduce anything (no kernel), and a
 * walkthrough asserts determinism the way D-191 makes it assertable: through
 * WHICH units were sampled, ordered by `serial_code` ascending. A field that
 * no reader can use is the stub D-026 forbids, one field wide.
 *
 * **THE SERVER SEAM, NAMED HERE SO IT IS NOT DISCOVERED AS AN EMPTY KEY.**
 * `apps/api/src/modules/quality/quality.serializer.ts` HAND-WRITES
 * `QualityInspectionJson` instead of inferring it from this schema — a
 * deliberate 07-A choice this module keeps — so adding `sample_cohort_id` and
 * `sampling` here neither makes the serializer emit them nor breaks its
 * typecheck. Both fields are stamped there by 07-D's SERVICE sub-step, in the
 * same change as the `QC_RECEIVING_PORT` binding that writes the columns.
 * Until then this is the contract that sub-step and the §4.3 queue are built
 * AGAINST — which is what a schema sub-step is for — and not a promise any
 * code path already keeps.
 */
export const QualityInspectionSampling = z
  .object({
    /** The plan actually run — see `QualityAppliedSampleMode` for `lot_whole`. */
    mode: QualityAppliedSampleMode,
    /** N — the cohort's candidate count AT SAMPLE TIME. Non-null only under
     * `aql`. */
    lot_size: z.number().int().nullable(),
    /** n — how many units were drawn. Equals `lot_size` where ISO 2859-1's
     * `n >= N` rule collapses the plan to 100 % inspection (D-196, resolved as
     * an explicit branch rather than left to emerge). Non-null only under
     * `aql`. */
    n: z.number().int().nullable(),
    /** Ac — failures at or below which the cohort is accepted. **Zero is a
     * real, common value** (`accept 0` is the whole of the small-lot corner
     * every KazKabel receipt lands in), so a client must render 0 and not
     * treat it as absent. Non-null only under `aql`. */
    accept_number: z.number().int().nullable(),
    /** Re — failures at or above which the cohort is rejected. Always greater
     * than `accept_number`; under NORMAL inspection it is exactly
     * `accept_number + 1`, which is a property of the normal-inspection table
     * (asserted over all 96 cells in the kernel, D-196) rather than of this
     * field — reduced inspection, named in §3.3 as successor work, carries
     * plans where it is not. Non-null only under `aql`. */
    reject_number: z.number().int().nullable(),
  })
  .openapi("QualityInspectionSampling");

/** One execution of a control point against a subject. The five subject columns
 * naming an ABSENT module — `receipt_line_id`/`supplier_id` (10),
 * `shipment_line_id` (09) — are published NULL rather than hidden, so those
 * modules fill them additively without changing a client (§0.1; the treatment
 * module 04 gave `sales_order_line_id`). */
export const QualityInspection = z
  .object({
    id: Uuid,
    inspection_no: z.string().regex(/^QI-\d{4}-\d{5,}$/),
    facility_id: Uuid,
    control_point_id: Uuid,
    /** Denormalized so a queue row renders without joining the definition. */
    control_point_code: QualityControlPointCode,
    /** The PINNED version's name, in all four locales (0042, defect #128). The
     * bundle rather than a resolved string, because THIS is what the floor queue
     * renders and the floor is where a Russian operator reads it — the office
     * queue, the run header and the Quarantine Board resolve the same bundle the
     * same way. Of the PINNED version, never the current one (M07-S12). */
    control_point_name_i18n: QualityLocaleName,
    /** Its `en` — the pre-0042 flat field. */
    control_point_name: z.string(),
    control_point_version: z.number().int(),
    stage: QualityStage,
    status: QualityInspectionStatus,
    resolution_status: QualityResolutionStatus,
    resolution_kind: QualityResolutionKind.nullable(),
    origin: QualityInspectionOrigin,
    retest_of_inspection_id: Uuid.nullable(),
    /** The RE-OPENED operation, never a spawned one (D-123 ruling 2). */
    rework_operation_id: Uuid.nullable(),
    is_blocking: z.boolean(),
    stock_unit_id: Uuid.nullable(),
    lot_id: Uuid.nullable(),
    receipt_line_id: Uuid.nullable(),
    work_order_id: Uuid.nullable(),
    work_order_operation_id: Uuid.nullable(),
    shipment_line_id: Uuid.nullable(),
    supplier_id: Uuid.nullable(),
    material_id: Uuid.nullable(),
    product_id: Uuid.nullable(),
    /** The machine grain is the WORK CENTRE (D-073, applied a third time). */
    work_center_id: Uuid.nullable(),
    /** Who MADE the subject — §3.7 attributes the defect here, not to the shift
     * that FOUND it. NULL when no production history names one, because a wrong
     * shift looks like an answer and a null does not (D-088 ruling 2). */
    produced_shift_instance_id: Uuid.nullable(),
    detected_shift_instance_id: Uuid.nullable(),
    inspector_user_id: Uuid.nullable(),
    failure_cause_id: Uuid.nullable(),
    quantity_inspected: QualityQty.nullable(),
    notes: z.string().nullable(),
    started_at: Timestamp.nullable(),
    completed_at: Timestamp.nullable(),
    cancelled_reason: z.string().nullable(),
    /**
     * THE COHORT (§3.3, D-192/D-203) — the receipt's sample, as one matched
     * control point saw it. NULL on every in-process and final inspection and
     * on every row written before slice 07-D.
     *
     * It is a FLAT field rather than part of `sampling` because the two facts
     * are independent by construction, and the DB grain says so
     * (`qc_inspections_sample_size_grain`'s one-directional clause, migration
     * 0040): §3.5c's `inspect_all` escalation mints one `origin='manual'`
     * inspection per UN-SAMPLED unit of the cohort, and those rows belong to
     * the cohort while being no part of the sampler's plan — folding the key
     * inside the plan object would hide them from the very grouping they exist
     * to complete.
     *
     * It is also the key a queue groups on, which is the second half of
     * D-192's own argument for the echo: five inspections that arrived
     * together are one decision, and a screen that cannot see that renders
     * five unexplained rows.
     *
     * Not `lot_id` and not `receipt_line_id`, because neither can carry §3.3's
     * arithmetic: `receipt_line_id` is module 10's and is NULL on every row
     * this deployment writes, and `lot_id` is a SUPERSET — module 06's
     * adoption lets one lot span several deliveries, so a second delivery's
     * sample would be counted against the first delivery's accept number.
     */
    sample_cohort_id: Uuid.nullable(),
    /** The resolved plan, echoed (D-192). NULL when no sampler ran — every
     * in-process and final inspection, every pre-07-D row, and the
     * cohort-bearing `inspect_all` sweep, which has a cohort and no plan. */
    sampling: QualityInspectionSampling.nullable(),
    created_at: Timestamp,
    updated_at: Timestamp,
  })
  .openapi("QualityInspection");

/** Module 04's §3.1 work-order lifecycle, as `rework_target_work_order_status`
 * reports it (D-299 ruling G5). RESTATED rather than imported from the
 * production contract — the rule this file states for `QualityFailureCauseCode`
 * — so a change to module 04's catalog cannot silently widen what this read
 * publishes. Only `released` and `in_progress` are live: §3.5a's rework
 * re-opens nothing on an order in any of the other four. */
export const QualityReworkTargetWorkOrderStatus = z.enum([
  "draft",
  "released",
  "in_progress",
  "completed",
  "closed",
  "cancelled",
]);

/** Detail (§5) — the inspection, the check items OF THE PINNED VERSION, and the
 * results recorded so far. The check items travel with the response because an
 * open inspection must grade against the version it pinned even after a newer
 * one exists (M07-S12), so a client that fetched the CURRENT definition would
 * render the wrong bounds. */
export const QualityInspectionDetailResponse = z
  .object({
    inspection: QualityInspection,
    check_items: z.array(QualityCheckItem),
    results: z.array(QualityInspectionResult),
    /** §3.5d's guided-cut eligibility, computed server-side (D-158, slice
     * 07-C). ADDITIVE — a new response property is not an oasdiff break — and
     * it is the field the §4.4 failure-action sheet's fifth button and the
     * per-item fault-position input both render from, so neither is ever a
     * client-side derivation. */
    guided_cut: QualityInspectionGuidedCut,
    /**
     * The §3.1 status of the WORK ORDER that owns this inspection's own
     * `work_order_operation_id` — the operation a `rework` with no explicit
     * ref re-opens (D-299 ruling G5). ADDITIVE, like `guided_cut`.
     *
     * It exists so the §4.4 failure-action sheet and the §4.6 disposition
     * dialog (whose single-selection probe reads this same route) can render
     * Rework DEAD WITH ITS REASON when the order is not `released`/
     * `in_progress`, instead of live and answered by `409
     * QUALITY_REWORK_TARGET_NOT_REOPENABLE` (`blocked_by:
     * work_order_not_live`) after the tap. That refusal is the common §3.5a
     * case: an in-process inspection is executed hours after the completion
     * that triggered it.
     *
     * NULL when there is no production target to report — the inspection
     * names no operation (a unit, a lot or a bare work-order subject), so a
     * rework needs an explicit `work_order_operation_id` this read cannot
     * know in advance. Null never means "live". The value is a READ: the
     * order can complete after it, and the server's refusal stays the
     * authority.
     */
    rework_target_work_order_status: QualityReworkTargetWorkOrderStatus.nullable(),
  })
  .openapi("QualityInspectionDetailResponse");

/** Why §3.5a's re-open would refuse — module 04's re-open core's two reasons
 * (spec 04 §3.2, D-299 ruling 2), the same values the resolve and disposition
 * doors' `409 QUALITY_REWORK_TARGET_NOT_REOPENABLE` carry as `blocked_by`.
 * Restated rather than imported, for the reason `QualityReworkTargetWorkOrderStatus`
 * states. */
export const QualityReworkBlockedBy = z.enum(["work_order_not_live", "operation_not_completed"]);

/** WHICH §3.5 door a rework would go through (D-299 ruling G2 / owner ruling
 * Q4): `inspection_resolve` is `POST /v1/quality/inspections/{id}/resolve`
 * (§4.4's sheet), `hold_disposition` is `POST /v1/quality/holds/{id}/
 * disposition` (§4.6's dialog). The same two values the refusals'
 * `continuations[].door` carry. */
export const QualityFailureWorkflowDoor = z.enum(["inspection_resolve", "hold_disposition"]);

/**
 * `GET /v1/quality/inspections/{id}/rework-target` (D-299 R8) — the step a
 * `rework` on this inspection would re-open, chosen by the rework door's own
 * rule (the explicit `work_order_operation_id` query, else the inspection's
 * own operation), and module 04's verdict on it.
 *
 * It exists because §4.4's failure sheet takes an EXPLICIT target, and the
 * door decides on THAT step's order. The detail's
 * `rework_target_work_order_status` describes the inspection's own step only,
 * so it cannot tell the sheet whether a rework sent back to a different step
 * — a final-stage failure returned to the step that made it — can land.
 *
 * `blocked_by` is null exactly when module 04's core would re-open the step
 * now; otherwise it names the reason the door would refuse under, so a
 * client renders Rework dead with the door's own reason and keeps no copy of
 * §3.1's live set. A READ: the order can complete after it, and the door's
 * refusal stays the authority.
 *
 * PRINCIPAL-AWARE SINCE D-299 ROUND 4 (owner ruling Q4 and the round-3
 * refutation). The read answers for THIS caller at `door`: `permitted` is
 * whether the caller passes every authority layer that door checks for this
 * rework — on a held step, `production.qc_hold.release` too (D-133) — and
 * `holder_roles` names the seed roles that do, which is D-133's who-sentence
 * when `requires_hold_release` is true. It is the same predicate the doors'
 * refusals use for `continuations[].permitted`, so the sheet can no longer
 * tell a planner "Rework will reopen step N" on a held step the door refuses.
 * ADDITIVE: four new required properties on a response, which oasdiff does
 * not count as breaking.
 */
export const QualityReworkTargetResponse = z
  .object({
    inspection_id: z.guid(),
    /** The step the rework would re-open — the query's, else the inspection's. */
    work_order_operation_id: z.guid(),
    work_order_id: z.guid(),
    seq_no: z.number().int(),
    /** Module 04's operation status, quoted as the doors quote it. */
    operation_status: z.string(),
    qc_hold: z.boolean(),
    /** The §3.1 status of the order that owns THIS step. */
    work_order_status: QualityReworkTargetWorkOrderStatus,
    blocked_by: QualityReworkBlockedBy.nullable(),
    /** The door `permitted` was judged at — the query's `door`, else
     * `inspection_resolve`. */
    door: QualityFailureWorkflowDoor,
    /** Whether THIS caller may send this rework through `door`: the door's
     * route code, the hold door's role-lineage narrowing (D-224), and
     * `production.qc_hold.release` when the step is held (D-133). Says
     * nothing about `blocked_by`, which is decided first. */
    permitted: z.boolean(),
    /** The seed roles whose grants take this rework at `door` (spec 07 §7) —
     * the names a refusal would give. */
    holder_roles: z.array(z.string()),
    /** The step is held, so the re-open also takes
     * `production.qc_hold.release` (D-133). A fact about the act, not the
     * caller; with `permitted: false` it says which authority is missing. */
    requires_hold_release: z.boolean(),
  })
  .openapi("QualityReworkTargetResponse");

export const QualityInspectionListResponse = z
  .object({ items: z.array(QualityInspection), next_cursor: Cursor })
  .openapi("QualityInspectionListResponse");

/**
 * Ad-hoc creation (§3.2's "Manual creation", §5). The subject refs are plain
 * ids validated through the OWNING module's service API — never a foreign key
 * (D-123 ruling 11) — and at least one physical/work subject is required: an
 * inspection of nothing is not an inspection.
 *
 * `client_idempotency_key` is the floor outbox's replay identity (§9.1) and has
 * a column of its own here, unlike the result write: an offline device can
 * CREATE the same inspection twice and the second create must return the first
 * row rather than a second inspection number. The first row comes back only to
 * the user who created it, and only for the same create (D-303).
 */
export const QualityInspectionCreateRequest = z
  .object({
    control_point_id: Uuid,
    facility_id: Uuid,
    stock_unit_id: Uuid.optional(),
    lot_id: Uuid.optional(),
    work_order_id: Uuid.optional(),
    work_order_operation_id: Uuid.optional(),
    product_id: Uuid.optional(),
    material_id: Uuid.optional(),
    work_center_id: Uuid.optional(),
    quantity_inspected: QualityQty.optional(),
    notes: z.string().max(2000).optional(),
    client_idempotency_key: z.string().min(1).max(200).optional(),
  })
  .superRefine((body, ctx) => {
    if (
      body.stock_unit_id === undefined &&
      body.lot_id === undefined &&
      body.work_order_id === undefined &&
      body.work_order_operation_id === undefined
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["stock_unit_id"],
        message:
          "an inspection needs a subject: one of stock_unit_id, lot_id, work_order_id or work_order_operation_id (spec 07 §2.5)",
      });
    }
  })
  .openapi("QualityInspectionCreateRequest");

export const QualityInspectionCreateResponse = z
  .object({
    inspection: QualityInspection,
    /** The pinned version's checklist, so the floor can execute immediately. */
    check_items: z.array(QualityCheckItem),
    /** §9.1 — true when `client_idempotency_key` had already been used; the
     * inspection returned is the ORIGINAL row. */
    idempotent_replay: z.boolean(),
  })
  .openapi("QualityInspectionCreateResponse");

/** Complete (§3.4). `failure_cause_id` is mandatory when any required item is
 * `fail` — the database refuses a failed inspection without one. */
export const QualityInspectionCompleteRequest = z
  .object({
    failure_cause_id: Uuid.optional(),
    notes: z.string().max(2000).optional(),
  })
  .openapi("QualityInspectionCompleteRequest");

/**
 * §5's "status + side-effect summary". Both side-effect fields were published
 * by 07-A ahead of their writers, for the reason module 04 published
 * `planned_order_id`: the later slice turns the behaviour on by writing a
 * value, changing no client.
 *
 *  * `hold_id` — LIVE since 07-B-1: §3.4's `auto_hold_on_fail` places a §2.7
 *    quarantine and this names it. Still null when the point does not
 *    auto-hold, when the subject has no stock, or when the D-129 skip rule
 *    left the subject unquarantinable.
 *  * `released_stock_unit_ids` — the receiving release (§3.3's sibling rule) is
 *    slice 07-D's, so the array is empty until then.
 */
export const QualityInspectionCompleteResponse = z
  .object({
    inspection: QualityInspection,
    hold_id: Uuid.nullable(),
    released_stock_unit_ids: z.array(Uuid),
  })
  .openapi("QualityInspectionCompleteResponse");

export const QualityInspectionCancelRequest = z
  .object({ reason: z.string().min(1).max(500) })
  .openapi("QualityInspectionCancelRequest");

// ===========================================================================
// §2.7 — quarantine holds (slice 07-B-1: list, manual place, detail; slice
// 07-B-2 adds the §3.5 disposition/resolve DTOs in their own section below)
// ===========================================================================

/** §2.7's lifecycle: `active → released | disposed`. */
export const QualityHoldStatus = z.enum(["active", "released", "disposed"]);

/**
 * What happens to HELD STOCK (§2.7). Deliberately NOT module 04's
 * `defect_handoffs.disposition` vocabulary — three of the values are spelled
 * the same and none of them means the same thing (D-123 ruling 1, seam C1):
 * this one releases or disposes a quarantined physical subject; 04's decides
 * what happens to a shift's defective WORK, and neither writes the other's
 * row. The disposition route (07-B-2) takes exactly these values, and the
 * `proposed_*` quartet carries them too (§7's agent row on holds disposition
 * is ask-only — an agent PROPOSES, a human disposes).
 */
export const QualityHoldDisposition = z.enum([
  "release",
  "rework",
  "scrap",
  "downgrade",
  "return_to_supplier",
]);

/**
 * The disposition a hold row can CARRY once it is decided: the five a person
 * picks, plus `guided_cut`, which only the cut's own execute path writes when
 * it releases, cuts and disposes the record in one transaction (§3.5d, D-155
 * pt 5, migration 0039). Read-side only. The disposition request keeps the
 * five-value enum, so no client can ask for a cut through §4.6's dialog, and
 * `proposed_disposition` keeps it too because its CHECK was never widened (an
 * agent proposes a cut through `propose_guided_cut`, D-165).
 */
export const QualityHoldTakenDisposition = z.enum([
  ...QualityHoldDisposition.options,
  "guided_cut",
]);

/** Integer minor units (platform money doctrine — the module-05 spelling). */
const MinorMoney = z.number().int();

/**
 * One quarantine record on a physical subject — a stock unit XOR a lot
 * (§2.7's `num_nonnulls = 1` CHECK). The USER-FACING word is QUARANTINE,
 * never "hold" (D-123 ruling 9): the wire keeps `hold` because renaming a
 * path is an oasdiff break, and every rendered string says quarantine.
 */
export const QualityHold = z
  .object({
    id: Uuid,
    /** Per-tenant sequence `QH-{YYYY}-{seq5}` (§2.7, same series service as
     * `inspection_no`). */
    hold_no: z.string().regex(/^QH-\d{4}-\d{5,}$/),
    facility_id: Uuid,
    stock_unit_id: Uuid.nullable(),
    lot_id: Uuid.nullable(),
    /** NULL for manually placed holds (§2.7). */
    inspection_id: Uuid.nullable(),
    /** Appended to — never replaced — when a second request attaches to an
     * already-active hold on the same subject (§2.7). */
    reason: z.string(),
    status: QualityHoldStatus,
    /**
     * THE MONEY PAIR — D-126 (owner, 2026-08-17) + §7. OPTIONAL, NEVER
     * NULLABLE: absence is the one spelling for BOTH "redacted" and "unknown",
     * and BOTH keys go absent together — a redacted response carries neither
     * `value_at_risk_minor` nor `currency` (§7's literal "field absent"),
     * which is a deliberate, documented divergence from module 05's
     * always-present `currency` (spec 07 §7 states it). Visible to
     * group_admin / facility_admin / finance / qc / planner — the originating
     * module governs its own number, role-keyed in the serializer, no
     * field-grant code, and module 05's `WASTE_COST_ROLES` is unchanged.
     */
    value_at_risk_minor: MinorMoney.optional(),
    /** Char-3; absent exactly when `value_at_risk_minor` is absent (D-126). */
    currency: z.string().length(3).optional(),
    placed_by: Uuid,
    placed_at: Timestamp,
    /** The TAKEN disposition, written by the disposition route (07-B-2) or
     * as `guided_cut` by the cut's execute path (§3.5d). Null on every
     * still-active hold. */
    disposition: QualityHoldTakenDisposition.nullable(),
    /** Required for `release` (justification) and `downgrade` (§2.7). */
    disposition_notes: z.string().nullable(),
    disposition_by: Uuid.nullable(),
    disposition_at: Timestamp.nullable(),
    /** An agent's PROPOSED disposition (§7 ask-only) — advisory, cleared or
     * superseded by the human decision; never a state transition itself. */
    proposed_disposition: QualityHoldDisposition.nullable(),
    proposed_notes: z.string().nullable(),
    proposed_by: Uuid.nullable(),
    proposed_at: Timestamp.nullable(),
    created_at: Timestamp,
    updated_at: Timestamp,
  })
  .openapi("QualityHold");

export const QualityHoldListResponse = z
  .object({ items: z.array(QualityHold), next_cursor: Cursor })
  .openapi("QualityHoldListResponse");

/**
 * Manual placement (§5's "Manual hold" row). EXACTLY ONE subject —
 * `stock_unit_id` XOR `lot_id` — the same one-of refinement shape the
 * inspection create and module 06's receipt use, mirroring §2.7's CHECK.
 * Placing against a subject already under an active hold creates NO second
 * row: the service appends the reason to the existing hold and returns it
 * (§2.7 — the partial uniques make a second active hold unrepresentable).
 */
export const QualityHoldPlaceRequest = z
  .object({
    stock_unit_id: Uuid.optional(),
    lot_id: Uuid.optional(),
    reason: z.string().min(1).max(2000),
    /** Optional, as on the inspection create: the subject's own facility is
     * authoritative (module 06 owns it); when sent it is cross-checked, and a
     * mismatch is QUALITY_SUBJECT_INVALID. */
    facility_id: Uuid.optional(),
  })
  .superRefine((body, ctx) => {
    const hasUnit = body.stock_unit_id !== undefined;
    const hasLot = body.lot_id !== undefined;
    if (hasUnit === hasLot) {
      ctx.addIssue({
        code: "custom",
        path: ["stock_unit_id"],
        message:
          "a quarantine hold names exactly one subject: stock_unit_id XOR lot_id (spec 07 §2.7)",
      });
    }
  })
  .openapi("QualityHoldPlaceRequest");

/**
 * The linked inspection, compact (no such summary existed — 07-A publishes
 * only the full QualityInspection, whose check items and results a board
 * detail does not want). Field spellings are QualityInspection's own; `status`
 * IS the outcome (`passed`/`failed`), with the resolution pair beside it.
 */
export const QualityHoldInspectionSummary = z
  .object({
    id: Uuid,
    inspection_no: z.string().regex(/^QI-\d{4}-\d{5,}$/),
    control_point_code: QualityControlPointCode,
    /** The PINNED version's name, in all four locales (0042, defect #128). The
     * bundle rather than a resolved string, because THIS is what the floor queue
     * renders and the floor is where a Russian operator reads it — the office
     * queue, the run header and the Quarantine Board resolve the same bundle the
     * same way. Of the PINNED version, never the current one (M07-S12). */
    control_point_name_i18n: QualityLocaleName,
    /** Its `en` — the pre-0042 flat field. */
    control_point_name: z.string(),
    stage: QualityStage,
    status: QualityInspectionStatus,
    resolution_status: QualityResolutionStatus,
    resolution_kind: QualityResolutionKind.nullable(),
    failure_cause_id: Uuid.nullable(),
    completed_at: Timestamp.nullable(),
  })
  .openapi("QualityHoldInspectionSummary");

/** Detail (§5, added by 07-B decision D8 — deep links cannot be served from a
 * cursor list). `inspection` is null on a manually placed hold. */
export const QualityHoldDetailResponse = z
  .object({
    hold: QualityHold,
    inspection: QualityHoldInspectionSummary.nullable(),
  })
  .openapi("QualityHoldDetailResponse");

// ===========================================================================
// §3.5 — the disposition and resolution surface (slice 07-B-2)
// ===========================================================================

/**
 * `POST /v1/quality/holds/{id}/disposition` (§3.5c, §5). One decision, one
 * body: the disposition and its note, plus §3.5a's single kind-specific ref.
 *
 * THE NOTE RULE IS CONTRACT-LEVEL (§3.5c's table): `release` carries a
 * MANDATORY justification and `downgrade` a MANDATORY note — enforced here by
 * superRefine so the refusal is a 422 the dialog can point at the field,
 * mirroring the 0036 CHECK (`qc_holds_notes_grain`) that makes the pair
 * unrepresentable below it.
 *
 * `work_order_operation_id` is §3.5a's rework ref: a rework may be resolved
 * against an operation the hold's inspection did not itself subject (a
 * final-stage failure sent back to the step that made it), so a `rework`
 * disposition may name the operation to re-open when the inspection lacks one
 * — and ONLY a `rework` disposition may carry it (kind-specific by
 * construction, not by silent ignore). No other kind takes a field: a hold's
 * subject is a whole physical unit or lot, so scrap consumes it whole (a
 * PARTIAL scrap is the guided cut, 07-C), the UoM is always the subject's own
 * (§3.5b), and `return_to_supplier`'s module-10 refs are 07-D/module-10 work.
 */
export const QualityHoldDispositionRequest = z
  .object({
    disposition: QualityHoldDisposition,
    /** REQUIRED (non-empty) for `release` and `downgrade` (§3.5c); optional —
     * and still recorded — for the other three. */
    disposition_notes: z.string().min(1).max(2000).optional(),
    /** `rework` only (§3.5a): the operation to re-open when it is not the
     * hold's inspection's own. */
    work_order_operation_id: Uuid.optional(),
    /**
     * §3.5c's RECEIVING SCOPE ESCALATION (D-193) — receiving-stage holds only,
     * and refused elsewhere. Kind-specific by construction exactly as
     * `work_order_operation_id` above is, so this is a shape the request object
     * already carries rather than a new one; the difference is that
     * applicability turns on the HOLD's stage rather than on this body, so the
     * refusal is the service's and not this schema's.
     *
     * When a receiving inspection fails, the auto-hold lands on the SAMPLED
     * unit while the rest of the cohort stays fenced by `lots.qc_status`
     * (§3.3's lot-level suspicion). The disposition of such a hold is therefore
     * two decisions, not one, and this is the second:
     *
     * - **`sampled_only`** — the failure is judged isolated. Dispose the unit
     *   hold per its kind, then RELEASE THE LOT. **No sibling unit is written**
     *   — D-202: the siblings are `filled` throughout (no receipt path ever
     *   writes a unit `quarantined`), so a "release" of them would be refused
     *   `422 UNIT_NOT_AVAILABLE`. The release that matters is the cohort's, and
     *   it is the lot's row that was fencing them.
     * - **`whole_lot`** — the cohort is condemned. The chosen disposition is
     *   applied to the LOT by placing a new lot-scoped hold and disposing it
     *   with the same kind, which routes `scrap` and `return_to_supplier`
     *   through 07-B's existing `LotsService.rejectInTxn`. **The original
     *   hold's subject is NEVER mutated** — a hold's subject is its identity
     *   (`qc_holds_subject_grain` plus the two partial uniques) and
     *   `quality.hold_placed` has already told the world it was a unit, so an
     *   UPDATE that moved it would leave an append-only chain asserting the
     *   quarantine of something else. Two rows tell the true story: this bag
     *   was held; then the cohort was condemned.
     * - **`inspect_all`** — one `origin='manual'` inspection per un-sampled
     *   unit of the cohort, each unit released by its own passing inspection.
     *   **Not offered when the receipt was bulk** (D-194/D-118): there are no
     *   units to inspect, and absence is the right answer when a control is
     *   genuinely inapplicable.
     *
     * **`whole_lot`, not `whole_line`** — the rename is a ruling (D-193), not
     * tidying. There is no receipt line: `receipt_lines` is module 10's,
     * `receipt_line_id` is NULL on every row this deployment writes, and there
     * is no `receipts` table at all. A LOT is a SUPERSET of a receipt, because
     * adoption lets one lot take several deliveries — so the confirmation copy
     * must say **the lot**, in en and ru, and the operator is told that a lot
     * which took two deliveries condemns both BEFORE pressing rather than
     * after.
     */
    scope: z.enum(["sampled_only", "whole_lot", "inspect_all"]).optional(),
  })
  .superRefine((body, ctx) => {
    const noteMissing =
      body.disposition_notes === undefined || body.disposition_notes.trim() === "";
    if ((body.disposition === "release" || body.disposition === "downgrade") && noteMissing) {
      ctx.addIssue({
        code: "custom",
        path: ["disposition_notes"],
        message:
          body.disposition === "release"
            ? "a release carries a mandatory justification note (spec 07 §3.5c)"
            : "a downgrade carries a mandatory note (spec 07 §3.5c)",
      });
    }
    if (body.work_order_operation_id !== undefined && body.disposition !== "rework") {
      ctx.addIssue({
        code: "custom",
        path: ["work_order_operation_id"],
        message:
          "work_order_operation_id is the rework ref (spec 07 §3.5a) — it accompanies disposition='rework' only",
      });
    }
  })
  .openapi("QualityHoldDispositionRequest");

/** The §5 resolve row's three kinds — §3.5a (rework), §3.5b (scrap), §3.5e
 * (accept deviation). `hold_disposition` and `guided_cut` are NOT launchable
 * here: the former is the disposition route's own record, the latter 07-C's
 * cut flow. */
export const QualityResolveKind = z.enum(["rework", "scrap", "accept_deviation"]);

/**
 * `POST /v1/quality/inspections/{id}/resolve` (§3.5, §5). `kind` plus the
 * kind-specific fields §3.5 names — and ONLY those:
 *
 *  * `rework` (§3.5a) may name `work_order_operation_id`, the operation to
 *    re-open when it is not the inspection's own (the final-stage failure sent
 *    back to the step that made it). Omitted, the inspection's own operation
 *    is re-opened.
 *  * `scrap` (§3.5b) takes NO quantity and NO uom: the inspection's subject
 *    determines both — a stock unit is consumed whole by module 06 via module
 *    05's posting, the quantity passed to the ledger is the subject's own (or
 *    `quantity_inspected` for a work-order-operation subject), and the UoM is
 *    "the subject's own UoM" by §3.5b's literal table. A PARTIAL scrap of a
 *    length is the guided cut (07-C), not a resolve field.
 *  * `accept_deviation` (§3.5e) carries a MANDATORY note — enforced here so
 *    the 422 is contract-level.
 *
 * `notes` is §3.5b's "resolution note", relayed to module 05 on a scrap and
 * stored on every kind.
 */
export const QualityInspectionResolveRequest = z
  .object({
    kind: QualityResolveKind,
    /** REQUIRED (non-empty) for `accept_deviation` (§3.5e). */
    notes: z.string().min(1).max(2000).optional(),
    /** `rework` only (§3.5a). */
    work_order_operation_id: Uuid.optional(),
  })
  .superRefine((body, ctx) => {
    if (
      body.kind === "accept_deviation" &&
      (body.notes === undefined || body.notes.trim() === "")
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["notes"],
        message: "accept_deviation carries a mandatory note (spec 07 §3.5e)",
      });
    }
    if (body.work_order_operation_id !== undefined && body.kind !== "rework") {
      ctx.addIssue({
        code: "custom",
        path: ["work_order_operation_id"],
        message:
          "work_order_operation_id is the rework ref (spec 07 §3.5a) — it accompanies kind='rework' only",
      });
    }
  })
  .openapi("QualityInspectionResolveRequest");

/**
 * A module-05 scrap valuation relayed through a resolve response. THE FIELD
 * TRAVELS WITH ITS OWNER'S RULE (D-126, owner — spec 07 §7 bullet 2 as
 * amended): visibility is module 05's OWN redaction set (`WASTE_COST_ROLES` =
 * group_admin / facility_admin / finance — NOT this module's wider §7 set),
 * and the spellings are module 05's ledger spellings (`unit_cost_minor`,
 * `cost_minor`, `currency`) so the number is recognisably the same number.
 * The WHOLE object is absent for a caller outside that set, when the kind is
 * not `scrap`, and when module 05's waterfall left the row uncosted (unknown
 * ≠ zero, §9.10). Absence stays absence — no `scrap_valuation_redacted`
 * marker on the wire; the label is client-side copy on the resolve screen,
 * which knows the role (§7).
 */
export const QualityScrapValuation = z
  .object({
    unit_cost_minor: MinorMoney,
    cost_minor: MinorMoney,
    currency: z.string().length(3),
  })
  .openapi("QualityScrapValuation");

/**
 * §5's "→ resolution refs" — what the §3.5 workflow actually produced, each
 * ref null when the kind produces none. Spellings are the shipped wire's own:
 * `hold_id` (the complete response's), `rework_operation_id` (§2.5's RE-OPENED
 * operation, never a spawned one — D-123 ruling 2), `waste_event_id` (module
 * 05's ledger row).
 *
 *  * `rework` → `rework_operation_id`; `hold_id` when the inspection's hold
 *    was disposed `rework` in the same transaction (T3), else null.
 *  * `scrap` → `waste_event_id` (+ `scrap_valuation` per its own rule);
 *    `hold_id` when a hold was disposed with it (T4).
 *  * `accept_deviation` → all three refs null: no stock action (§3.5e); a
 *    still-active hold stays active and is disposed from the board.
 *
 * `resolution_status` is `resolved` on every 200 — carried anyway so the
 * client updates its row without a second read (the pair moves atomically,
 * 0034's `qc_inspections_resolution_grain`).
 */
export const QualityResolutionResponse = z
  .object({
    inspection_id: Uuid,
    resolution_kind: QualityResolutionKind,
    resolution_status: QualityResolutionStatus,
    hold_id: Uuid.nullable(),
    rework_operation_id: Uuid.nullable(),
    waste_event_id: Uuid.nullable(),
    scrap_valuation: QualityScrapValuation.optional(),
  })
  .openapi("QualityResolutionResponse");

// ===========================================================================
// §2.8 / §2.9 / §3.5d — THE GUIDED CUT (slice 07-C)
//
// One cut event per failed length-based inspection, at most one LIVE at a time
// (D-162), tiled into segments A / waste / B for a SINGLE fault (D-171).
//
// THREE FACTS ABOUT THIS SURFACE THAT ARE EASY TO GET WRONG, stated once:
//
//  1. LENGTHS RIDE AT TWO SCALES ON PURPOSE (D-156). Every measured length —
//     the source, and every segment's `from_m`/`to_m`/`length_m` — is
//     `numeric(18,6)`, module 06's own scale, because §9.6 makes module 06's
//     measured length the authority. `fault_position_m` and `trim_allowance_m`
//     stay `numeric(12,3)`: an operator's metre mark and a control-point
//     setting. Segment A is then 3 dp and segment B carries the 6-dp
//     remainder, so the tiling stays EXACT.
//  2. THE TILING IS EXACT BECAUSE THE CUT IS MODELLED KERF-FREE (D-170).
//     Module 06's `planGuidedCut` charges kerf INSIDE the segments; module 07
//     omits `kerf` entirely, and the trim window IS the physical allowance. A
//     1.0 m trim swallows two blade widths many times over, and charging a
//     millimetre inside a tape-measure number would be false precision that
//     also makes the tiling inexact. The recorded consequence: the cut is
//     under-accounted by two blade widths, absorbed by module 06's
//     `cut_tolerance_pct` exactly as any other measuring imprecision.
//  3. THERE IS NO `waste_event_id` ANYWHERE ON THIS WIRE (D-157) — not on a
//     segment, not on the execute response, not in `quality.cut_completed`.
//     Module 05 mints the trim-waste ledger row in a POST-COMMIT consumer, so
//     nothing can know it at execute time, and §5 may not promise a field the
//     response cannot carry. `inventory_cut_event_id` — module 06's own outbox
//     event id — is the deterministic join key that replaces it.
// ===========================================================================

/** §2.8's lifecycle: `proposed → confirmed → completed`, or `cancelled` from
 * either live state. The cut screen's double-confirm is the `confirmed →
 * completed` step (§4.5), and §9.2's optimistic guard refuses an execute that
 * is not `confirmed` — the same shape the hold disposition uses. */
export const QualityCutStatus = z.enum(["proposed", "confirmed", "completed", "cancelled"]);

/** §2.9 — a segment becomes a child STOCK unit, or a WASTE piece module 06
 * posts to module 05's ledger. A stock segment shorter than
 * `min_sellable_length_m` is proposed as `waste` instead (§3.5d step 1); a NULL
 * minimum imposes none (D-172). */
export const QualityCutSegmentKind = z.enum(["stock", "waste"]);

/**
 * One segment of a cut event (§2.9), A-end first by `seq`.
 *
 * `new_stock_unit_id` and `retest_inspection_id` are NULL on a PROPOSED
 * segment and stamped at execute steps 6 and 7 — which is why §2.9's kind↔ref
 * CHECK is ONE-DIRECTIONAL (a waste segment never carries either; a stock
 * segment carries them according to where it stands in the lifecycle). A
 * biconditional would make propose impossible.
 */
export const QualityCutSegment = z
  .object({
    id: Uuid,
    seq: z.number().int(),
    kind: QualityCutSegmentKind,
    from_m: QualityMeasuredLengthM,
    to_m: QualityPositiveMeasuredLengthM,
    length_m: QualityPositiveMeasuredLengthM,
    /** The child unit module 06 minted for this stock segment — `null` until
     * execute, and always null on a waste segment. */
    new_stock_unit_id: Uuid.nullable(),
    /** The retest inspection minted for this stock segment (`origin =
     * 'cut_retest'`, graded against the PARENT's pinned control-point version
     * — D-164, never `is_current`). Null until execute, always null on waste. */
    retest_inspection_id: Uuid.nullable(),
  })
  .openapi("QualityCutSegment");

/** One guided-cut execution (§2.8). */
export const QualityCutEvent = z
  .object({
    id: Uuid,
    facility_id: Uuid,
    inspection_id: Uuid,
    source_stock_unit_id: Uuid,
    /** Module 06's MEASURED length at propose time (§9.6 — never
     * machine-claimed). Execute re-reads it and answers `409
     * QUALITY_CUT_LENGTH_CHANGED` with `current_length_m` if a re-measure
     * moved it (§9.3). */
    source_length_m: QualityPositiveMeasuredLengthM,
    fault_position_m: QualityPositiveLengthM,
    /** Copied from the pinned control point; operator-overridable UPWARD ONLY
     * (§5's propose row) — a narrower trim would leave fault inside a
     * sellable half. */
    trim_allowance_m: QualityLengthM,
    status: QualityCutStatus,
    /** The source's genealogy depth AT PROPOSE TIME (original = 0, D-158), so
     * the row records WHY the cut was allowed rather than leaving §9.4's cap
     * to be re-derived later against a chain that has since grown. */
    genealogy_depth: z.number().int(),
    /** Module 06's `inventory.unit_cut` OUTBOX EVENT ID, stamped at execute
     * (D-157). NULL before execute. Module 05's ledger row for a waste segment
     * is `client_event_id = derivedUuid("waste:unit_cut:{this}:trim:{seq}")` —
     * stated for readers and analytics, never a query this module runs (§6:
     * module 07 reads no waste table). */
    inventory_cut_event_id: Uuid.nullable(),
    confirmed_by: Uuid.nullable(),
    confirmed_at: Timestamp.nullable(),
    /** Why a live cut was abandoned — including D-162's supersede, where a new
     * propose cancels the existing live cut inside its own transaction. */
    cancelled_reason: z.string().nullable(),
    cancelled_at: Timestamp.nullable(),
    executed_by: Uuid.nullable(),
    completed_at: Timestamp.nullable(),
    created_at: Timestamp,
    updated_at: Timestamp,
  })
  .openapi("QualityCutEvent");

/** §5's cut detail, and the body of propose / confirm / cancel: the §2.8 row
 * plus its segments in `seq` order. One shape for four routes because each of
 * them answers the same question — what is this cut, and how is it tiled. */
export const QualityCutEventDetailResponse = z
  .object({
    cut_event: QualityCutEvent,
    segments: z.array(QualityCutSegment),
  })
  .openapi("QualityCutEventDetailResponse");

/**
 * `POST /v1/quality/inspections/{id}/cut-events` (§5) — propose.
 *
 * THERE IS NO SOURCE UNIT ON THIS WIRE, and that is load-bearing (D-155 pt 8):
 * the source is the INSPECTION'S OWN SUBJECT, which is what makes the execute
 * path's cut-child test — "the source is the `new_stock_unit_id` of a
 * `qc_cut_segments` row whose `retest_inspection_id` IS the cut's own
 * inspection" — a statement about this inspection rather than about any unit a
 * caller could name.
 *
 * `fault_position_m` is OPTIONAL: omitted, it defaults to the LOWEST fault
 * position among the inspection's FAILED results, tie-broken by check-item
 * `seq` (D-171) — the first fault an operator walking the cable from the A-end
 * meets, and a rule somebody can predict rather than whichever row the query
 * returned first. 07-C is SINGLE-FAULT: two faults are cut one at a time, the
 * first cut's segment B carrying the second.
 *
 * `trim_allowance_m` is OPTIONAL and **upward only** — validated SERVICE-side
 * against the pinned point's `cut_trim_allowance_m`, which is not on this body
 * and which a contract refinement therefore cannot see. A narrower trim would
 * leave the fault inside a half sold as good.
 *
 * A propose SUPERSEDES any live cut on the same inspection (D-162).
 */
export const QualityCutProposeRequest = z
  .object({
    fault_position_m: QualityPositiveLengthM.optional(),
    trim_allowance_m: QualityLengthM.optional(),
  })
  .openapi("QualityCutProposeRequest");

/** One operator adjustment to a proposed segment (§5's "segment
 * adjustments[]"). OUTWARD ONLY — a handle may move away from the fault, never
 * inward past the `fault ± trim` window (§4.5, §3.5d step 2) — and the server
 * RE-TILES from the adjusted boundaries rather than trusting the client's
 * arithmetic, which §4.5 calls a courtesy and never the authority. */
export const QualityCutSegmentAdjustment = z
  .object({
    seq: z.number().int().min(0).max(64),
    from_m: QualityMeasuredLengthM,
    to_m: QualityPositiveMeasuredLengthM,
  })
  .openapi("QualityCutSegmentAdjustment");

/**
 * `POST /v1/quality/cut-events/{id}/confirm` (§5) — `proposed → confirmed`.
 *
 * `adjustments` is OPTIONAL because accepting the proposal unchanged is the
 * ordinary case: the server proposed the tiling and the operator agreed. When
 * present it is re-validated (outward-only, exact tiling, no stock segment
 * below the minimum) and re-tiled server-side; the refusals are §9.3's three
 * 422s.
 */
export const QualityCutConfirmRequest = z
  .object({
    adjustments: z.array(QualityCutSegmentAdjustment).max(16).optional(),
  })
  .superRefine((body, ctx) => {
    const seqs = (body.adjustments ?? []).map((a) => a.seq);
    if (new Set(seqs).size !== seqs.length) {
      ctx.addIssue({
        code: "custom",
        path: ["adjustments"],
        message: "one adjustment per segment: seq values must be unique (spec 07 §3.5d step 2)",
      });
    }
    for (const [i, a] of (body.adjustments ?? []).entries()) {
      if (Number(a.to_m) <= Number(a.from_m)) {
        ctx.addIssue({
          code: "custom",
          path: ["adjustments", i, "to_m"],
          message: "a segment is an interval: to_m must exceed from_m (spec 07 §2.9)",
        });
      }
    }
  })
  .openapi("QualityCutConfirmRequest");

/**
 * `POST /v1/quality/cut-events/{id}/execute` (§5) — the ONE transaction, and
 * §5's three named response fields.
 *
 * `waste_event_id` IS NOT HERE (D-157): module 05 mints that ledger row in a
 * post-commit consumer off `inventory.unit_cut`, so no synchronous response
 * can carry it. `inventory_cut_event_id` is the deterministic join key and
 * §2.9 states the derivation.
 *
 * `cut_event` and `segments` ride along beside the three named fields so the
 * screen renders the terminal row without a second read — the same courtesy
 * the disposition response gives the board.
 */
export const QualityCutExecuteResponse = z
  .object({
    cut_event: QualityCutEvent,
    segments: z.array(QualityCutSegment),
    /** Module 06's child units, in segment `seq` order — one per `kind:
     * 'stock'` segment. */
    new_stock_unit_ids: z.array(Uuid),
    /** The retests minted for those children (`origin = 'cut_retest'`), same
     * order. A passing retest RELEASES its unit back to `filled` (D-176). */
    retest_inspection_ids: z.array(Uuid),
    /** Module 06's outbox event id for this cut (D-157). */
    inventory_cut_event_id: Uuid,
  })
  .openapi("QualityCutExecuteResponse");

/** `POST /v1/quality/cut-events/{id}/cancel` (§5) — abandon a `proposed` or
 * `confirmed` cut. The reason is MANDATORY and stored: a cut somebody staged
 * over a customer's cable and then dropped, with no reason, is indistinguishable
 * from a bug. Service-narrowed to {group_admin, facility_admin, qc} on top of
 * the single code (D-163 — a floor_worker who proposes is never stranded,
 * because D-162's supersede rule means their next propose replaces the live
 * one). */
export const QualityCutCancelRequest = z
  .object({ cancelled_reason: z.string().min(1).max(500) })
  .openapi("QualityCutCancelRequest");

// ===========================================================================
// §5 — the ship-readiness gate (a READ with no caller yet)
// ===========================================================================

/**
 * Per-unit gate status. Module 09 does not exist, so nothing calls this today —
 * and that is exactly why it is built as a READ: it is the whole of the
 * contract module 09 will consume, it is drivable by API test now, and M07-S05
 * is amended to assert the flip from `ready: false` to `ready: true` across an
 * inspection rather than a ship confirmation that has no module (§0.1).
 *
 * `ready` = the latest FINAL inspection passed, or failed with resolution
 * `accept_deviation`, or its hold was released with disposition `release` —
 * and no active hold covers the unit or its lot. `hold_ids` carries the
 * active holds since 07-B-1; the released-hold disjunct is 07-B-2's (the
 * D-129 pending note — unreachable until the disposition route mounted).
 */
export const QualityShipReadinessRow = z
  .object({
    stock_unit_id: Uuid,
    ready: z.boolean(),
    blocking_inspection_ids: z.array(Uuid),
    hold_ids: z.array(Uuid),
  })
  .openapi("QualityShipReadinessRow");

export const QualityShipReadinessResponse = z
  .object({ items: z.array(QualityShipReadinessRow) })
  .openapi("QualityShipReadinessResponse");

// ===========================================================================
// problem codes (spec 07 §5 / §9)
// ===========================================================================

/**
 * The RFC 9457 `code`s slices 07-A and 07-B-1's endpoints can raise. Every
 * entry is raisable by a route a shipped slice registers — the module-05
 * discipline, both directions: a code raised in a handler that is not here is
 * a refusal no client can parse, and a code here that no handler can raise is
 * a documented refusal the product never gives.
 */
// ═══════════════════════════════════════════════════════════════════════════
//  07-E — CERTIFICATES OF ANALYSIS / CONFORMANCE (§2.10, §2.11, §3.6)
// ═══════════════════════════════════════════════════════════════════════════

export const QualityCoaKind = z.enum(["analysis", "conformance"]).openapi("QualityCoaKind");
export type QualityCoaKindT = z.infer<typeof QualityCoaKind>;

/** The languages a certificate can be drafted in: the locales whose server
 * text is written (SERVER_TEXT_LOCALES). A certificate's chrome is server text
 * and its PDF embeds only the fonts those locales need, so a Hindi or
 * Traditional Chinese certificate waits for L4's text and fonts and is
 * refused until then. §2.11's column CHECK is wider (the registry's six), so
 * widening this enum is an additive change with no migration. */
export const QualityCoaLanguage = z
  .enum(SERVER_TEXT_LOCALES as unknown as readonly [ServerTextLocale, ...ServerTextLocale[]])
  .openapi("QualityCoaLanguage");

export const QualityCoaDocumentStatus = z
  .enum(["draft", "issued", "revoked"])
  .openapi("QualityCoaDocumentStatus");

/** §2.10 — a branding + layout definition. `product_id` NULL = the tenant's
 * default template; a product-bound template outranks it at auto-pick. */
export const QualityCoaTemplate = z
  .object({
    id: Uuid,
    name: z.string(),
    product_id: Uuid.nullable(),
    kind: QualityCoaKind,
    logo_file_id: Uuid.nullable(),
    header_i18n: z.record(z.string(), z.string()),
    footer_i18n: z.record(z.string(), z.string()),
    signatory_name: z.string().nullable(),
    signatory_title_i18n: z.record(z.string(), z.string()).nullable(),
    active: z.boolean(),
  })
  .openapi("QualityCoaTemplate");
export type QualityCoaTemplateT = z.infer<typeof QualityCoaTemplate>;

/** §5's one template mutation row is CREATE/UPDATE in one door: `id` present =
 * update that template (name/branding/binding/active), absent = create. */
export const QualityCoaTemplateUpsertRequest = z
  .object({
    id: Uuid.optional(),
    name: z.string().min(1).max(200),
    product_id: Uuid.nullable().optional(),
    kind: QualityCoaKind,
    logo_file_id: Uuid.nullable().optional(),
    header_i18n: z.record(z.string(), z.string()).optional(),
    footer_i18n: z.record(z.string(), z.string()).optional(),
    signatory_name: z.string().max(200).nullable().optional(),
    signatory_title_i18n: z.record(z.string(), z.string()).nullable().optional(),
    active: z.boolean().optional(),
  })
  .openapi("QualityCoaTemplateUpsertRequest");

export const QualityCoaTemplateListResponse = z
  .object({ templates: z.array(QualityCoaTemplate) })
  .openapi("QualityCoaTemplateListResponse");

/** §3.6 step 3's missing-data guard, one row per (unit, appears-on-CoA item)
 * that lacks a PASSED measurement. `reason` distinguishes "never measured"
 * from "measured but the inspection did not pass". */
export const QualityCoaGap = z
  .object({
    stock_unit_id: Uuid.nullable(),
    unit_label: z.string().nullable(),
    check_item_id: Uuid,
    check_item_name: z.string(),
    control_point_id: Uuid,
    control_point_code: z.string(),
    reason: z.enum(["missing_measurement", "not_passed"]),
  })
  .openapi("QualityCoaGap");
export type QualityCoaGapT = z.infer<typeof QualityCoaGap>;

/** §2.11 — one certificate revision. `public_url` is non-null only while
 * `status='issued'` (§9.11: revocation deletes the token, so a revoked
 * revision has NO link, not a dead one). `data_snapshot` stays server-side;
 * the wire carries the rendered PDF and the gap report instead. */
export const QualityCoaDocument = z
  .object({
    id: Uuid,
    coa_no: z.string(),
    revision: z.number().int(),
    template_id: Uuid,
    kind: QualityCoaKind,
    facility_id: Uuid,
    lot_id: Uuid.nullable(),
    shipment_id: Uuid.nullable(),
    customer_id: Uuid.nullable(),
    /** Printed-text customer identity frozen into the snapshot at draft —
     * module 09's `customer_id` FK does not exist yet (§0.1), and a
     * certificate names its customer as INK, not as a foreign key. */
    customer_name: z.string().nullable(),
    language: QualityCoaLanguage,
    status: QualityCoaDocumentStatus,
    pdf_file_id: Uuid.nullable(),
    public_url: z.string().nullable(),
    issued_at: isoDateTime({ offset: true }).nullable(),
    revoked_reason: z.string().nullable(),
    created_at: isoDateTime({ offset: true }),
  })
  .openapi("QualityCoaDocument");
export type QualityCoaDocumentT = z.infer<typeof QualityCoaDocument>;

export const QualityCoaDraftRequest = z
  .object({
    template_id: Uuid,
    /** Every P2 certificate is drafted from a LOT (§0.1's fence; the
     * shipment door arrives with module 09, additively). */
    lot_id: Uuid,
    language: QualityCoaLanguage,
    kind: QualityCoaKind,
    customer_name: z.string().min(1).max(200).optional(),
    /** §3.6 step 3's "exclude units" — the excluded units are omitted from
     * the snapshot AND from the gap check. */
    excluded_stock_unit_ids: z.array(Uuid).max(500).optional(),
  })
  .openapi("QualityCoaDraftRequest");

export const QualityCoaDraftResponse = z
  .object({ document: QualityCoaDocument, gaps: z.array(QualityCoaGap) })
  .openapi("QualityCoaDraftResponse");

export const QualityCoaDocumentListResponse = z
  .object({
    /** §5-preamble pagination (review F17): the page + its keyset cursor. */
    items: z.array(QualityCoaDocument),
    next_cursor: z.string().nullable(),
    /** DEPRECATED — mirrors `items`. The first ship's shape, held because the
     * additive-contract gate keeps every published field; retires with the
     * next major. */
    documents: z.array(QualityCoaDocument),
  })
  .openapi("QualityCoaDocumentListResponse");

export const QualityCoaIssueResponse = z
  .object({ document: QualityCoaDocument })
  .openapi("QualityCoaIssueResponse");

export const QualityCoaRevokeRequest = z
  .object({
    reason: z.string().min(1).max(2000),
    /** §3.6 step 5: revoke-and-reissue drafts `revision + 1` under the SAME
     * `coa_no` in the same transaction. */
    reissue: z.boolean(),
  })
  .openapi("QualityCoaRevokeRequest");

export const QualityCoaRevokeResponse = z
  .object({
    document: QualityCoaDocument,
    /** The `revision + 1` draft when `reissue: true`, else null. */
    next_revision: QualityCoaDocument.nullable(),
  })
  .openapi("QualityCoaRevokeResponse");

// ═══════════════════════════════════════════════════════════════════════════
//  07-E — THE ANALYTICS READS (§2.12, §5's four rows + the D-216-B door)
// ═══════════════════════════════════════════════════════════════════════════

/** One §2.12 bucket. `key` is the group_by dimension's id (day /
 * product_id / work_center_id / produced_shift_instance_id) — NULL when the
 * row's dimension is unstamped; the client translates the label (no server
 * UI strings cross the wire). */
export const QualityFpyBucket = z
  .object({
    key: z.string().nullable(),
    total: z.number().int(),
    passed_first_time: z.number().int(),
    /** 4-dp text, or NULL when the bucket saw zero first-time inspections —
     * absence of a yield is not a 0 % yield. */
    fpy: z.string().nullable(),
  })
  .openapi("QualityFpyBucket");

export const QualityFpyResponse = z
  .object({
    buckets: z.array(QualityFpyBucket),
    /** §4.8's staleness read — when the projection last refreshed (D-216-B). */
    refreshed_at: isoDateTime({ offset: true }),
  })
  .openapi("QualityFpyResponse");

/** §7's money-pair rule, verbatim: `value_at_risk_minor` and `currency`
 * ABSENT TOGETHER for a caller outside the D-126 value set — and ALSO absent
 * when the cause's window holds MIXED currencies (§9.10 forbids summing
 * across them; a mixed window has no honest single number) or no stamped
 * hold money at all. */
export const QualityParetoCause = z
  .object({
    code: z.string(),
    name: z.string(),
    count: z.number().int(),
    value_at_risk_minor: z.number().int().optional(),
    currency: z.string().length(3).optional(),
  })
  .openapi("QualityParetoCause");

export const QualityParetoResponse = z
  .object({ causes: z.array(QualityParetoCause) })
  .openapi("QualityParetoResponse");

export const QualityShiftScoreboardRow = z
  .object({
    shift_instance_id: z.guid().nullable(),
    /** The shift's code + tenant-localized name; NULL on the unattributed
     * row (inspections carrying no shift stamp) — the client translates. */
    shift_label: z.string().nullable(),
    inspections: z.number().int(),
    defects_produced: z.number().int(),
    defects_caught: z.number().int(),
    handoff_incidents: z.number().int(),
    fpy: z.string().nullable(),
  })
  .openapi("QualityShiftScoreboardRow");

export const QualityShiftScoreboardResponse = z
  .object({
    date: z.string(),
    facility_id: z.guid(),
    rows: z.array(QualityShiftScoreboardRow),
  })
  .openapi("QualityShiftScoreboardResponse");

export const QualitySupplierReceivingRow = z
  .object({
    supplier_id: z.guid(),
    receipts: z.number().int(),
    failures: z.number().int(),
    /** 4-dp text, failures/receipts. */
    failure_rate: z.string(),
    top_cause: z.string().nullable(),
  })
  .openapi("QualitySupplierReceivingRow");

export const QualitySupplierReceivingResponse = z
  .object({ suppliers: z.array(QualitySupplierReceivingRow) })
  .openapi("QualitySupplierReceivingResponse");

/** The D-216-B admin door's answer. `refreshed` is false only when the
 * debounce held (never for this route — the admin door forces). */
export const QualityAnalyticsRefreshResponse = z
  .object({
    refreshed: z.boolean(),
    refreshed_at: isoDateTime({ offset: true }),
  })
  .openapi("QualityAnalyticsRefreshResponse");

export const qualityProblemCodes = [
  // --- control points + their definition surface (§2.1–§2.3, §5 rows 1–5) ---
  "QUALITY_CONTROL_POINT_NOT_FOUND",
  /** UNIQUE (tenant, code, version) — the code is the stable identity across
   * versions, so a second point may not claim it. */
  "QUALITY_CONTROL_POINT_CODE_TAKEN",
  /** A new version was requested against a superseded row (§3.1 step 5 edits
   * the CURRENT one), so the chain would fork. */
  "QUALITY_CONTROL_POINT_NOT_CURRENT",
  /** An inspection was requested against a deactivated or superseded point
   * (§3.1 step 6: deactivating stops future triggering). */
  "QUALITY_CONTROL_POINT_INACTIVE",
  /** §2.2's stage rules: a receiving binding without material/supplier or with
   * a product, a final binding without a product, or ANY binding on an
   * in-process point (which is bound on module 01's routing step instead). */
  "QUALITY_BINDING_STAGE_INVALID",
  /**
   * §2.1's sampling grain — a mode without its parameters, or parameters
   * without their mode.
   *
   * **From 07-D it is also the RECEIVING sampler's refusal (D-194)**, raised
   * out of `POST /v1/inventory/receipts` when a BULK receipt (no `stock_units`
   * at all) meets a receiving control point whose plan is `aql`: ISO 2859-1 has
   * no reading for a lot size expressed in kilograms, and silently degrading to
   * "inspect the lot" would fabricate an acceptance number the factory then
   * acts on. Same code because it is the same fact — this point's plan cannot
   * be applied to this delivery — and the remedy is the same shape: receive it
   * as serialized units, or change the point's plan. The extension
   * `sampling_refusal` discriminates the arm for a client that wants to.
   */
  "QUALITY_SAMPLING_PLAN_INVALID",
  /**
   * D-197's honesty gate, reached from `POST /v1/inventory/receipts`: the AQL
   * cell this lot size resolves to is one of the FIFTEEN the MIL-STD-105E
   * second keying could not confirm, so `resolvePlan` refuses to state an
   * accept number rather than state one that may be wrong.
   *
   * **A DISTINCT CODE, not `QUALITY_SAMPLING_PLAN_INVALID`, and the reason is
   * the i18n rule rather than taxonomy.** The API never carries translated
   * prose (D-022): a client renders localized copy keyed on `code`, so two
   * refusals sharing a code get ONE Russian sentence. These two need different
   * ones — the bulk arm's remedy is *"receive it as serialized units"* and this
   * one's is *"set the point to `fixed_count`, or have quality confirm the
   * accept number against the standard"*. Nothing about the point's
   * configuration is wrong here, and telling a storekeeper it is would send
   * them to the wrong screen.
   *
   * Out of reach for the design partner: it fires only above 3,200 units at
   * level III, 10,000 at level II or 150,000 at level I, and every KazKabel
   * receipt is far below all three. The extensions name the code letter, the
   * AQL and the lot size so quality can look the cell up.
   */
  "QUALITY_AQL_PLAN_UNCONFIRMED",
  /**
   * #127 / D-210 — the CONFIGURATION-TIME mate of the two receipt-door codes
   * above, raised from the §5 control-point create/version routes (the pack
   * seeder retains-and-reports instead of raising it; a seed must not die
   * half-way through a tenant's catalog): a `receiving` point with an `aql`
   * plan whose bindings resolve to one or more products, EVERY one of which is
   * bulk-grained (`tracking` ∈ {none, lot}). No receipt of those materials can
   * ever present a discrete unit to sample, so the point would install and sit
   * unapplicable until the first delivery went red — D-204's four months,
   * refused at the door it came through this time.
   *
   * A DISTINCT CODE for the D-022 reason the pair above states: the audience is
   * the person CONFIGURING the point (quality-catalog), not the storekeeper at
   * the bay, and the localized remedy names BOTH outs — change the point's
   * sampling plan, or change the material's `tracking` in the catalogue so its
   * stock arrives as measured units. The extensions carry `skus` and
   * `product_ids` so the screen can link the materials.
   */
  "QUALITY_SAMPLING_PLAN_UNAPPLICABLE",
  /** §2.3 — a measurement item without a uom or a bound, min > max, a checklist
   * item with no rows, or duplicate `seq`. */
  "QUALITY_CHECK_ITEM_INVALID",
  "QUALITY_CHECK_ITEM_NOT_FOUND",
  // --- failure causes (§2.4, §5 rows 7–8) ----------------------------------
  "QUALITY_FAILURE_CAUSE_NOT_FOUND",
  "QUALITY_FAILURE_CAUSE_CODE_TAKEN",
  // --- vertical pack seeding (§3.1, §5 row 6) ------------------------------
  /** The requested pack is not one this platform ships. */
  "QUALITY_VERTICAL_PACK_UNKNOWN",
  // --- inspections (§2.5, §3.4, §5 rows 9–14) ------------------------------
  "QUALITY_INSPECTION_NOT_FOUND",
  /** A result or completion against an inspection that is already terminal
   * (passed/failed/cancelled) — §3.4's state machine has no such transition.
   * From 07-B-2 it also refuses a `resolve` whose inspection is not
   * `failed` with `resolution_status='pending'` (a passed, cancelled or
   * already-resolved inspection launches no §3.5 workflow). */
  "QUALITY_INSPECTION_NOT_ACTIONABLE",
  /** §9.1's two-device race: the second `complete` loses and is told who won,
   * so the PWA can discard its queued failure action instead of duplicating it. */
  "QUALITY_INSPECTION_ALREADY_COMPLETED",
  /** Completion attempted while a REQUIRED check item has no result (§3.4). */
  "QUALITY_INSPECTION_INCOMPLETE",
  /** Completion as failed without a cause (§3.4 makes it mandatory). */
  "QUALITY_FAILURE_CAUSE_REQUIRED",
  /** A measurement item recorded without `measured_value` — there is nothing to
   * grade (§2.6). */
  "QUALITY_MEASUREMENT_REQUIRED",
  /** The one override §3.4 forbids: flipping a COMPUTED fail to pass. The
   * reverse (a computed pass failed by eye, with a note) is accepted — M07-S09
   * asserts both halves. */
  "QUALITY_RESULT_OVERRIDE_FORBIDDEN",
  /** A `photo_file_ids` element does not resolve in the file registry for this
   * tenant with purpose `quality_evidence` and `status='stored'`, or fewer
   * photos than `min_photos` were supplied for a `photo_required` item. */
  "QUALITY_EVIDENCE_INVALID",
  /** A subject ref does not resolve through its owning module, or none was
   * given. Raised as this module's own code rather than re-minting module 06's
   * — the seam hands back its own refusals (the rule 04's gate states). */
  "QUALITY_SUBJECT_INVALID",
  /** The facility does not exist or is outside the principal's fence. */
  "FACILITY_NOT_FOUND",
  // --- quarantine holds (§2.7, §3.5, §5 — slices 07-B-1 and 07-B-2) ---------
  /** The hold does not exist for this tenant (cross-tenant and out-of-fence
   * ids are invisible, the 07-A rule restated). */
  "QUALITY_HOLD_NOT_FOUND",
  /** §9.2's optimistic guard: every terminal transition is one
   * `UPDATE … WHERE status='active'`, and a zero-row result means somebody
   * else already decided. The refusal NAMES THE WINNER — its disposition, its
   * disposer and its instant — so §4.6's "already disposed by {user} {time}
   * ago" state renders from the problem body without a second read (the
   * `DEFECT_HANDOFF_ALREADY_DISPOSED` shape, module 04's precedent). */
  "QUALITY_HOLD_NOT_ACTIVE",
  /** The C6 double-count fence (§3.5b, D-123 ruling 6): a `defect_handoffs`
   * row disposed `scrap` already covers this work-order operation, so module
   * 04 has already retro-attributed these metres and a second posting would
   * double-count an append-only ledger. REFUSE, NEVER NET — the refusal names
   * the hand-off, who disposed it and when, and offers the two honest
   * continuations (`accept_deviation`, or dispose the quarantine
   * `rework`/`downgrade`). */
  "QUALITY_SCRAP_ALREADY_ATTRIBUTED",
  /** The planner's-rework rule (§3.5a, 07-B-2 decision D-133 closing D-123
   * ruling 3's OPEN case): a rework by a principal WITHOUT
   * `production.qc_hold.release` on an operation whose `qc_hold` is set would
   * leave a re-opened step nobody can work. Refused, naming the operation,
   * the hold's setting inspection where this module's rows attribute one, and
   * the role set that may clear the hold first. The same call on an UNHELD
   * operation proceeds normally. */
  "QUALITY_REWORK_REQUIRES_HOLD_RELEASE",
  /** The rework no-op honesty rule (§3.5a, 07-B-2 fix round R4): module 04's
   * re-open transition is a deliberate no-op for any non-`completed` step,
   * and a resolve that answered 200 claiming a re-open that never happened
   * would record a re-do nobody can perform (D-118's honesty principle — a
   * silent null is worse than a refusal). Refused NAMING the operation's
   * actual status; nothing about the resolution stands. */
  "QUALITY_REWORK_TARGET_NOT_REOPENABLE",
  /** D7 (07-B-2 decision D-132): module 05's `DEFECT_SCRAP` category carries
   * `requires_cause = true`, and the inspection's failure cause has no
   * `waste_cause_code` mapping in the vertical pack. The refusal NAMES THE
   * FAILURE CAUSE so an admin can map it — never a literal `"UNKNOWN"`
   * fallback, which no pack seeds and which would 404 inside the consumer. */
  "QUALITY_SCRAP_CAUSE_UNMAPPED",
  /** §2.7's partial uniques (`qc_holds_active_unit_idx` /
   * `qc_holds_active_lot_idx`) reached under the D-129 subject advisory lock:
   * an active quarantine already covers this subject. TWO ARMS, one code —
   * the manual door's fence-hidden hold (placed outside this session's facility
   * scope, so the attach read cannot see it and cannot append to it), and
   * D-193's `whole_lot` escalation meeting a lot that already carries an active
   * hold. CATALOGUED HERE BY 07-D-4b: the code has been raised since 07-B-1
   * (`holds.service.ts`'s insert translation) and was never listed — a gap this
   * module has no parity gate to have caught, unlike modules 01/02/04/05/06. */
  "QUALITY_HOLD_ALREADY_ACTIVE",
  // --- §3.5c's receiving scope escalation (D-193 — slice 07-D-4b) -----------
  /** A receiving-stage quarantine on a SAMPLED UNIT of a cohort was
   * dispositioned without a `scope`. §3.5c makes the choice part of the
   * decision, not an option on it: the auto-hold fences one bag while
   * `lots.qc_status` fences the rest, so a disposition that answered only the
   * bag would leave the cohort fenced with nothing left to decide it. The
   * refusal names the three scopes and what each does. */
  "QUALITY_SCOPE_REQUIRED",
  /** A `scope` was sent where §3.5c has no choice to make, or the chosen one is
   * genuinely inapplicable. Three arms, each named in the sentence: the hold is
   * not a receiving-stage one; the hold is LOT-scoped already (a BULK receipt
   * has no units, so D-194 gives it one lot-subject inspection and its
   * disposition is cohort-grain by construction — there is no sample to
   * separate from its siblings); or `inspect_all` was chosen and the cohort has
   * no un-sampled unit left to inspect. D-118's rule at the API: a control that
   * cannot apply is refused by name, never accepted and silently narrowed. */
  "QUALITY_SCOPE_NOT_APPLICABLE",
  // --- the guided cut (§2.8, §2.9, §3.5d, §9.3/§9.4/§9.6 — slice 07-C) ------
  //
  // UPPER_SNAKE WITH A `QUALITY_CUT_` PREFIX, and §9.3's lower-case
  // `length_changed` is corrected in the spec rather than copied here (D-160).
  // §5's preamble has always mandated "stable UPPER_SNAKE `code` values" and
  // the shipped completion race already answers
  // `QUALITY_INSPECTION_ALREADY_COMPLETED` — the lower-case spellings were
  // stale prose about correct code, not a description of it.
  /** The cut event does not exist for this tenant (cross-tenant and
   * out-of-fence ids are invisible — the 07-A rule restated). Not in D-160's
   * enumerated set, which lists the REFUSALS; a surface with four routes
   * keyed on `{id}` needs a not-found code the same way every other one in
   * this catalog does. */
  "QUALITY_CUT_EVENT_NOT_FOUND",
  /** §3.5d's eligibility fails at propose: the pinned point does not carry
   * `allow_guided_cut`, the subject is not a length-measured stock unit, no
   * failed result carries a `fault_position_m`, or the inspection is not
   * `failed` with its resolution still pending. The `guided_cut.reason` on the
   * inspection read names WHICH — the refusal and the screen answer the same
   * question with the same vocabulary. */
  "QUALITY_CUT_NOT_ELIGIBLE",
  /** §9.4's recursion cap, defined once in D-158: the ORIGINAL unit is depth
   * 0, its children 1, and a propose is refused when
   * `genealogy_depth(source) >= 5`. Deeper failures force scrap / downgrade /
   * rework — the fence against infinite salami-slicing of a systematically
   * faulty cable. At the cap the floor control is DEAD WITH ITS REASON, never
   * absent (D-167): depth is not a permission fact. */
  "QUALITY_CUT_DEPTH_CAP",
  /** §9.3: the segments do not tile `[0, source_length_m]` EXACTLY. Exact is
   * possible — and required — because a guided cut is modelled kerf-free
   * (D-170). */
  "QUALITY_CUT_SEGMENTS_DO_NOT_TILE",
  /** §9.3: a `kind='stock'` segment is shorter than the pinned point's
   * `min_sellable_length_m` and must be re-marked waste. A NULL minimum
   * imposes none and this is unraisable for such a point (D-172). */
  "QUALITY_CUT_SEGMENT_BELOW_MINIMUM",
  /** §9.3: the fault does not lie inside the waste window
   * `[fault − trim, fault + trim]` after the operator's outward-only
   * adjustments — i.e. a handle was dragged INWARD past the fault, which would
   * sell the fault inside a half. */
  "QUALITY_CUT_FAULT_OUTSIDE_WINDOW",
  /** §9.6: the source carries only a MACHINE-CLAIMED length (extending
   * machines lie — claims 6000 m, actual 5500–6400 m), so there is no measured
   * length for the cut maths to be authoritative against. The cut is blocked
   * until the unit is re-measured; on the floor the control is dead with its
   * reason AND the re-measure offered IN PLACE (D-167/D-118), never a link to
   * another screen. */
  "QUALITY_CUT_LENGTH_NOT_MEASURED",
  /** §9.2's optimistic guard on execute, the same shape as the hold race:
   * `status` is not `confirmed`. A `confirmed` cut event sitting over
   * physically-split stock is the failure §3.5d's execute ordering exists to
   * prevent, and this is its first step. */
  "QUALITY_CUT_NOT_CONFIRMED",
  /** §9.3: module 06 reports a different current MEASURED length than the
   * `source_length_m` the cut was planned against — a re-measure happened
   * between propose and execute. `current_length_m` rides in the problem
   * extensions so the screen recomputes rather than guessing. This 409 exists
   * BECAUSE the source length can go stale, which is exactly the staleness a
   * queued cut could not detect — the argument D-166 makes the whole surface
   * online-only on. */
  "QUALITY_CUT_LENGTH_CHANGED",
  /** D-155 case (a): an active LOT hold covers the source's lot. This
   * reproduces `releaseStockOrSkipInTxn`'s own skip rule (§2.7's
   * overlapping-hold rule) — a unit stays quarantined while its cohort is
   * condemned, and a cut that ignored it would launder a lot-level decision
   * into two sellable children. The refusal names the lot hold. */
  "QUALITY_CUT_LOT_HELD",
  /**
   * D-155 case (c): an active UNIT hold placed by a DIFFERENT inspection, or
   * by hand. The material carries a second unresolved quality question, and
   * cutting it would strand that question on an `empty` parent row and answer
   * it by destroying the evidence. The refusal names the hold, its reason and
   * its author; the continuation takes seconds — dispose or release the other
   * quarantine on the board (`quality.hold.disposition`), then cut.
   */
  "QUALITY_CUT_SUBJECT_HELD",
  /**
   * D-155 case (d): the source is `quarantined` with NO `qc_holds` row behind
   * it and is NOT the cut child THIS VERY INSPECTION was minted to retest.
   *
   * THE OPERATIVE TEST IS A CONJUNCTION of two facts the execute transaction
   * already holds, both in module-07-owned rows — quoted from §3.5d because
   * the spec warns these two glosses must not drift: **the source is the
   * `new_stock_unit_id` of a `qc_cut_segments` row whose `retest_inspection_id`
   * IS the cut's own inspection, AND that inspection carries
   * `origin='cut_retest'`.** Both conjuncts, never either alone: each implies
   * the other given `origin`'s single writer (execute step 7) and the fact
   * that a propose derives its source from the inspection's own subject, so an
   * `OR` over two spellings of one fact opens if EITHER spelling drifts and is
   * strictly weaker than either half.
   *
   * A DISTINCT CODE rather than reusing `QUALITY_CUT_SUBJECT_HELD` (D-155 pt
   * 7): there is no `qc_holds` row to name, and the continuation is a
   * different door taken by a different principal — module 06's own
   * `release-unit` under `inventory.quarantine`, by whoever quarantined the
   * unit and for their own reason. A 409 that named a hold the reader then
   * cannot find would send them to an empty board.
   */
  "QUALITY_CUT_SUBJECT_QUARANTINED",
  // ── 07-E: the CoA lifecycle ─────────────────────────────────────────────
  /** UNIQUE (tenant_id, name) on §2.10 — 409 naming the taken name. */
  "QUALITY_COA_TEMPLATE_NAME_TAKEN",
  /** §2.10's service-side validations an FK cannot see: a `logo_file_id`
   * that is not a stored `quality_coa_logo` file, a `product_id` module 01
   * does not know, or an update targeting a template that does not exist. */
  "QUALITY_COA_TEMPLATE_INVALID",
  /** §3.6 step 1's subject check: the lot does not exist, carries no stock
   * units to certify, or the template is inactive. */
  "QUALITY_COA_SUBJECT_INVALID",
  /** §3.6 step 3's guard AT ISSUE: `appears_on_coa` items lack a passed
   * measurement for covered units. The gap rows ride in `extensions.gaps` —
   * the same shape the draft response carries — so the composer lists them
   * without a second read. `kind='conformance'` alone may proceed past
   * `missing_measurement` gaps with pass attestations (§3.6 step 3); a
   * `not_passed` gap blocks BOTH kinds — attesting conformance over a failed
   * measurement would be a false certificate, not a thinner one. */
  "QUALITY_COA_GAPS",
  /** The lifecycle guard (§9.2's optimistic shape): issue on a non-draft,
   * revoke on an already-revoked revision, or preview on a revoked one. */
  "QUALITY_COA_STATUS_INVALID",
] as const;

export type QualityProblemCode = (typeof qualityProblemCodes)[number];
